import React, { useState, useEffect, useCallback, useRef, lazy, Suspense, useMemo } from 'react';
import { apiFetch } from '../services/apiClient';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'motion/react';
import { useClock } from '../contexts/ClockContext';
import { HardcoreBootSequence } from './HardcoreBootSequence';
import { socketService } from '../services/socketService';
import { fetchUserSettings } from '../services/userSettings';
import { GlobalNodeMap } from './GlobalNodeMap';
import { sounds } from '../services/soundService';
import {
  Rocket,
  Cpu,
  Globe,
  Settings as SettingsIcon,
  Shield,
  Zap,
  X,
  User as UserIcon,
  Search,
  Folder,
  FileText,
  Activity,
  Wifi,
  Volume2,
  VolumeX,
  Battery,
  Bluetooth,
  Moon,
  Minimize2,
  Minus,
  Square,
  ChevronRight,
  ArrowLeft,
  Bell,
  Disc,
  Headphones,
  Loader2,
  BrainCircuit,
  Sparkles,
  Box,
  Wrench,
  MessageSquare,
  Crown,
  Castle,
  Brush,
  Play,
  Pause,
  Mic,
  Terminal as TerminalIcon,
  Music,
  Bot,
  LayoutGrid,
  Monitor,
  Trash2,
  RefreshCw,
  Circle,
  Calendar,
  Camera,
  Copy,
  Download,
  Palette,
  TrendingUp,
  Workflow,
  FolderOpen,
  BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';
import { GlassCard } from './SharedUI';
import { LocalAgentSphere } from './LocalAgentSphere';
import { VoiceTrainingDialog } from './VoiceTrainingDialog';
import { VoicePicker, voiceDisplayName } from './VoicePicker';
import { VoiceForge } from './VoiceForge';
import { ToolPanel } from './ToolPanel';
import { TeamHub } from './TeamHub';
import { GitHubMCPBrowser } from './GitHubMCPBrowser';
import { TokenUsagePanel } from './TokenUsagePanel';
import { GrowthWorkbench } from './GrowthWorkbench';
import { CalendarPanel } from './CalendarPanel';
import { ScreenshotTool } from './ScreenshotTool';
import { SkillCenter } from './SkillCenter';
import { GlassesDeviceManager } from './GlassesDeviceManager';
import { NotificationCenter } from './NotificationCenter';
import { TokenDashboard } from './TokenDashboard';
import { SubscriptionPanel } from './SubscriptionPanel';
import { useContextMenu } from '@/hooks/useContextMenu';
import { useTheme } from '../contexts/ThemeProvider';
import { ContextMenu } from './ContextMenu';
import { DesktopOnboarding } from './DesktopOnboarding';
import { ContextualTooltips } from './desktop/ContextualTooltips';
import { DeviceSyncCenter } from './DeviceSyncCenter';
import { AgentChatPage } from './AgentChatPage';
import { ChatSidebar } from './ChatSidebar';
import { ProjectSpace } from './ProjectSpace';
import * as conversationService from '@/services/conversationService';
import { RuntimeLogPanel } from './RuntimeLogPanel';
import { OrgHub } from './org/OrgHub';
import { OrgPortal } from './OrgPortal';
import { WorkModeSwitch } from './org/WorkModeSwitch';
import { Sanctuary } from './Sanctuary';
import { MemoryAvatarLab } from './MemoryAvatarLab';
import { AvatarStudio } from './AvatarStudio';
import { ReminderPanel } from './ReminderPanel';
import { PetAvatar } from './SpriteAnimator';
import { getDefaultPets } from '../pets/defaults';
import type { PetConfig } from '../pets/types';
import { HelpModal } from './HelpModal';
import { NexusStatsPanel } from './NexusStatsPanel';
import { AgentDetailCard } from './AgentDetailCard';
import { NexusDrilldown, type DrillState } from './NexusDrilldown';
import { MeshSyncSelector } from './MeshSyncSelector';
import { useNexusPulse } from '@/hooks/useNexusPulse';
import { useSocket } from '@/hooks/useSocket';
import { useAmbientPoller } from '@/hooks/useAmbientPoller';
import { useVoiceCall } from '@/hooks/useVoiceCall';
import { useApp, type OperationMode } from '@/contexts/AppContext';
import { AutonomousFeed } from './AutonomousFeed';
import { SystemExplorer } from './SystemExplorer';
const NexusGlobeLazy = lazy(() => import('./NexusGlobe').then(m => ({ default: m.NexusGlobe })));
import WorkflowPanel, { type BackgroundWorkflowTask, type WorkflowStep } from './WorkflowPanel';
import { useWakeWord } from '../hooks/useWakeWord';
import { useSystemStats } from '../hooks/useSystemStats';
import { ErrorBoundary } from './ErrorBoundary';
import { ToolConfirmDialog } from './ToolConfirmDialog';
import { appConfirm } from '@/lib/appConfirm';

const KnowledgeBase = lazy(() => import('./KnowledgeBase').then(m => ({ default: m.KnowledgeBase })));
import { OSWindow } from './desktop/OSWindow';
import { ControlCenter } from './desktop/ControlCenter';
import { DesktopIcon } from './desktop/DesktopIcon';


import { KernelMonitorApp } from './desktop/KernelMonitorApp';
import { Spotlight } from './desktop/Spotlight';
import { ExecutionWorkQueue } from './desktop/ExecutionWorkQueue';
import { DailyPlans } from './desktop/DailyPlans';
import { SoundPanel } from './desktop/SoundPanel';
import { BatteryIndicator } from './desktop/BatteryIndicator';
import { MeetingModeButton } from './desktop/MeetingModeButton';
import { ThemeWidget } from './desktop/ThemeWidget';
import { MoreCenterGrid } from './desktop/MoreCenterGrid'; // v3.7.0
import { PersonalityEditor } from './PersonalityEditor';
import { AdminCenter } from './AdminCenter';
import { Settings } from './Settings';
import { TerminalWindow } from './Terminal';
import { MusicMoodLayer } from './MusicMoodLayer';
import { MusicCenter } from './MusicCenter';
import { useMusicPlayerSnapshot, useMusicVisible } from '../hooks/useMusicPlayer';
import { useVoiceprint } from '../hooks/useVoiceprint';
import { useFaceRecognition } from '../hooks/useFaceRecognition';
import { usePresence } from '../hooks/usePresence';
import { getSensorPermissionSnapshot, SENSOR_PERMISSIONS_CHANGED } from '@/services/sensorPermissionService';
import {
  archiveLegalMeetingToConsultationCase,
  clearLegalConsultationCaseId,
  getLegalCaseLabel,
  getLegalConsultationCase,
  getLegalConsultationCaseId,
} from '@/lib/legalCaseStore';
import { PresenceIndicator } from './biometrics/PresenceIndicator';
import { UserSwitchPrompt } from './biometrics/UserSwitchPrompt';
import { systemService } from '@/services/systemService';
import { usePlatform } from '@/hooks/usePlatform';
import { NeuralSynthesisMonitor } from './NeuralSynthesisMonitor';
import { MijiaPanel } from './MijiaPanel';
import { WearablePanel } from './WearablePanel';
import { CarPanel } from './CarPanel';
import { DesktopDock } from './DesktopDock';
import { DateWeatherWidget } from './DateWeatherWidget';
import { APP_VERSION } from '@/lib/version';

function resolvePetPreference(pet: any): PetConfig | null {
  if (!pet) return null;
  if (pet.atlas && pet.spritesheet) return pet as PetConfig;
  const defaults = getDefaultPets();
  return defaults.find(d => d.id === pet.id) || null;
}

function serializePetPreference(pet: PetConfig | null) {
  if (!pet) return null;
  return {
    id: pet.id,
    name: pet.name,
    author: pet.author,
    atlas: pet.atlas,
    spritesheet: pet.spritesheet,
    thumbnail: pet.thumbnail,
    palette: pet.palette,
    tags: pet.tags,
  };
}

type ClientPermissionSnapshot = Record<string, string | boolean | number | null | undefined>;
type ClientRuntimeSnapshot = {
  autostartSupported?: boolean;
  autostart启用d?: boolean;
  closeToBackground?: boolean;
  startedInBackground?: boolean;
  backendNodeRunning?: boolean;
  backendPythonRunning?: boolean;
  nodeRestarts?: number;
  pythonRestarts?: number;
  globalShortcut?: string;
  lastError?: string;
};

declare global {
  interface Window {
    xfElectron?: {
      getSystemInfo: () => Promise<{ platform: string; hostname: string; freeMemory: number }>;
      runCommand: (command: string) => Promise<{ success: boolean; output: string }>;
    };
  }
}
interface MeetingNote {
  id: string;
  text: string;
  time: number;
}

// MoreCenterGrid moved to src/components/desktop/MoreCenterGrid.tsx (v3.7.0)

export function DesktopUI({ 
  t, 
  user, 
  lang,
  setLang,
  activeTab, 
  setActiveTab, 
  onLogin, 
  renderTabContent 
}: { 
  t: any; 
  user: any; 
  lang: 'en' | 'zh';
  setLang: (l: 'en' | 'zh') => void;
  activeTab: string; 
  setActiveTab: (tab: string) => void; 
  onLogin: () => void;
  renderTabContent: (tab: string) => React.ReactNode;
}) {
  // Camera and Environment state
  const [viewMode, setViewMode] = useState<'personal' | 'world'>('personal');
  const [selectedAgent, setSelectedAgent] = useState<any>(null);
  const [syncRate, setSyncRate] = useState(1);
  // G 视图下钻状态机：中枢总览 → 组织/个人集群 → 智能体列表 → 单智能体详情
  const { pulses, bursts } = useNexusPulse();
  const [drill, setDrill] = useState<DrillState>({ level: 'overview' });
  const handleAgentNode = useCallback((agent: any) => {
    setSelectedAgent(agent);
    setDrill({ level: 'agent', agentId: agent.id });
  }, []);
  const cameraZ = useMotionValue(viewMode === 'personal' ? 0 : -800);

  useEffect(() => {
    cameraZ.set(viewMode === 'personal' ? 0 : -1000);
  }, [viewMode]);

  // Biometrics: face recognition + voiceprint activated via useFaceRecognition / useVoiceprint

  const personalScale = useTransform(cameraZ, [0, -1000], [1, 0.4]);
  const personalOpacity = useTransform(cameraZ, [0, -400], [1, 0]);
  const { isTauri } = usePlatform();
  const { selectedVoiceId, unreadCount, notifications, addNotification, orgConnection, workDomain, switchDomain, operationMode, setOperationMode, aiConfig } = useApp();

  // ✅ Bug fix: 'chat' 与 home/knowledge 同级排除——避免 activeTab effect 把聊天也开成 OSWindow 导致返回桌面残留空弹窗
  const [openWindows, setOpenWindows] = useState<string[]>(activeTab !== 'home' && activeTab !== 'knowledge' && activeTab !== 'chat' ? [activeTab] : []);
  const [minimizedWindows, setMinimizedWindows] = useState<string[]>([]);
  const [focusedWindow, setFocusedWindow] = useState<string | null>(activeTab !== 'home' && activeTab !== 'knowledge' && activeTab !== 'chat' ? activeTab : null);
  const [windowOrder, setWindowOrder] = useState<string[]>(activeTab !== 'home' && activeTab !== 'knowledge' && activeTab !== 'chat' ? [activeTab] : []);
  const [knowledgeOpen, setKnowledgeOpen] = useState(activeTab === 'knowledge');
  const [helpOpen, setHelpOpen] = useState(false);

  // ✅ Bug fix: 监听 activeTab 变化并同步 window 状态（解决路由无法打开 Settings 子页面）
  React.useEffect(() => {
    if (activeTab === 'knowledge') {
      setKnowledgeOpen(true);
      setOpenWindows([]);
      setFocusedWindow(null);
      setWindowOrder([]);
      return;
    }
    if (activeTab === 'home' || activeTab === 'chat') {
      setKnowledgeOpen(false);
      setOpenWindows([]);
      setFocusedWindow(null);
      setWindowOrder([]);
      return;
    }
    // 其他 tab → 打开对应 window
    if (!openWindows.includes(activeTab)) {
      setOpenWindows(prev => [...new Set([...prev, activeTab])]);
      setWindowOrder(prev => [...new Set([...prev, activeTab])]);
    }
    setFocusedWindow(activeTab);
    setKnowledgeOpen(false);
  }, [activeTab]);
  // v3.7.1: stable refs for ChatSidebar (replaces missing handleSelectConv/handleNewChat)
  // 🔴 v3.12.60: 加 setChatOpen(true) 修复首次点对话后 chat 区域不渲染 Bug
  const selectConv = React.useCallback((id: string | null) => { setSearchConvId(id); setCenterView('chat'); setChatOpen(true); }, []);
  const newChat = React.useCallback(() => { setSearchConvId(null); setCenterView('chat'); setChatOpen(true); }, []);
  const [chatOpen, setChatOpen] = useState(false);
  const handleAgentChat = useCallback((agent: any) => {
    setSelectedAgent(agent);
    setDrill({ level: 'overview' });
    setChatOpen(true);
    setActiveTab('chat');
  }, [setChatOpen, setActiveTab]);
  const [centerView, setCenterView] = useState<string>('chat');
  const [chatPrefill, setChatPrefill] = useState('');
  const [sanctuaryOpen, setSanctuaryOpen] = useState(false);
  const [sanctuaryAgent, setSanctuaryAgent] = useState<any>(null);
  const [petReaction, setPetReaction] = useState<{ animation: string; until: number } | null>(null);
  const [activePersonality, setActivePersonality] = useState('xiaofeng');
  const petReactionTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const triggerPetReaction = (animation: string, ms: number = 1500) => {
    if (petReactionTimeout.current) clearTimeout(petReactionTimeout.current);
    setPetReaction({ animation, until: Date.now() + ms });
    petReactionTimeout.current = setTimeout(() => setPetReaction(null), ms);
  };

  const [memoryLabOpen, setMemoryLabOpen] = useState(false);
  const [equippedAccessories, setEquippedAccessories] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('xf_accessories');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [selectedPet, setSelectedPet] = useState<PetConfig | null>(() => {
    try {
      const saved = localStorage.getItem('xf_selected_pet');
      if (saved) {
        const parsed = JSON.parse(saved);
        const resolved = resolvePetPreference(parsed);
        if (resolved) return resolved;
      }
    } catch {}
    // 默认无宠物 — 仅粒子特效，宠物需在形象设置中手动启用
    return null;
  });

  // Ref to prevent echoing our own preference changes back via socket
  const petPrefsSavingRef = useRef(false);
  const savePetPrefsToServer = useCallback(async (pet: PetConfig | null, accessories: string[]) => {
    const storedPet = serializePetPreference(pet);
    localStorage.setItem('xf_accessories', JSON.stringify(accessories));
    if (storedPet) {
      localStorage.setItem('xf_selected_pet', JSON.stringify(storedPet));
    } else {
      localStorage.removeItem('xf_selected_pet');
    }
    petPrefsSavingRef.current = true;
    try {
      await apiFetch('/api/preferences/pet', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pet: storedPet,
          accessories,
        }),
        credentials: 'include',
      });
    } catch {}
    setTimeout(() => { petPrefsSavingRef.current = false; }, 500);
  }, []);

  const { theme, setTheme, openThemeCenter } = useTheme();
  const [clientPermissions, setClientPermissions] = useState<ClientPermissionSnapshot>({});
  const [clientRuntime, setClientRuntime] = useState<ClientRuntimeSnapshot>({});
  const [isControlCenterOpen, setIsControlCenterOpen] = useState(false);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchConvId, setSearchConvId] = useState<string | null>(null);
  const [settingsSection, setSettingsSection] = useState('general');
  const [brightness, setBrightness] = useState(85);
  const [volume, setVolume] = useState(60);
  // v3.12.10: 用 ClockContext 替代 useState(new Date()) + setInterval，避免每秒全量重渲染
  const time = useClock();
  const [isWallpaperMode, setIsWallpaperMode] = useState(false);
  // 与 Settings.tsx 的 SettingsItem 约定保持一致：storageKey === 'true' 才开启，
  // 未设置(或 'false') 一律视为关闭。这样桌面右侧面板严格"跟着设置走"，且 localStorage 持久化不丢记忆。
  const [neuralSynthesis启用d, setNeuralSynthesis启用d] = useState(() => {
    try { return localStorage.getItem('xf_neural_synthesis_enabled') === 'true'; }
    catch { return false; }
  });
  const [mijia启用d, setMijia启用d] = useState(() => {
    try { return localStorage.getItem('xf_mijia_enabled') === 'true'; }
    catch { return false; }
  });
  // 首页运行日志开关：与 Settings 约定保持一致（storageKey === 'true' 才开启）
  const [wearable启用d, setWearable启用d] = useState(() => {
    try { return localStorage.getItem('xf_wearable_enabled') === 'true'; }
    catch { return false; }
  });
  const [carControl启用d, setCarControl启用d] = useState(() => {
    try { return localStorage.getItem('xf_carcontrol_enabled') === 'true'; }
    catch { return false; }
  });
  const [runtimeLog启用d, setRuntimeLog启用d] = useState(() => {
    try { return localStorage.getItem('xf_runtime_log_enabled') === 'true'; }
    catch { return false; }
  });
  // 首页日历开关：未设置=关闭
  const [calendar启用d, setCalendar启用d] = useState(() => {
    try { return localStorage.getItem('xf_calendar_enabled') === 'true'; }
    catch { return false; }
  });
  // 首页学习流开关：未设置=关闭
  const [learningFlow启用d, setLearningFlow启用d] = useState(() => {
    try { return localStorage.getItem('xf_learning_flow_enabled') === 'true'; }
    catch { return false; }
  });
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.key === 'xf_neural_synthesis_enabled') {
        setNeuralSynthesis启用d(detail.value === true);
      }
      if (detail?.key === 'xf_mijia_enabled') {
        setMijia启用d(detail.value === true);
      }
      if (detail?.key === 'xf_wearable_enabled') {
        setWearable启用d(detail.value === true);
      }
      if (detail?.key === 'xf_carcontrol_enabled') {
        setCarControl启用d(detail.value === true);
      }
      if (detail?.key === 'xf_runtime_log_enabled') {
        setRuntimeLog启用d(detail.value === true);
      }
      if (detail?.key === 'xf_calendar_enabled') {
        setCalendar启用d(detail.value === true);
      }
      if (detail?.key === 'xf_learning_flow_enabled') {
        setLearningFlow启用d(detail.value === true);
      }
    };
    window.addEventListener('xf:setting-changed', handler);
    return () => window.removeEventListener('xf:setting-changed', handler);
  }, []);
  const isWallpaperModeRef = useRef(false);
  const chatOpenRef = useRef(false);
  const closeToBackgroundSyncRef = useRef(false);
  const wallpaperAutomationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wallpaperWas启用dBeforeAutomationRef = useRef(false);
  const wallpaperWorkPromptTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [wallpaperWorkPromptVisible, setWallpaperWorkPromptVisible] = useState(false);
  const [wallpaper, setWallpaper] = useState<string>(() => localStorage.getItem('xf_wallpaper_type') || 'celestial');
  const [wallpaperUrl, setWallpaperUrl] = useState<string>(() => localStorage.getItem('xf_wallpaper_url') || '');
  const wallpaperInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    isWallpaperModeRef.current = isWallpaperMode;
    if (isWallpaperMode) setWallpaperWorkPromptVisible(false);
  }, [isWallpaperMode]);

  useEffect(() => {
    chatOpenRef.current = chatOpen;
    if (chatOpen) setWallpaperWorkPromptVisible(false);
  }, [chatOpen]);

  const getDefaultDesktopIconPosition = useCallback((index: number) => ({
    x: 44 + (index % 4) * 136,
    y: Math.floor(index / 4) * 124,
  }), []);

  // 桌面图标去重叠：自定义/默认坐标吸附到网格，冲突坐标自动右移避让，杜绝图标重叠
  const ICON_STEP_X = 136;
  const ICON_STEP_Y = 124;
  const ICON_ORIGIN_X = 44;
  const resolveDesktopIconPositions = (
    icons: { windowId: string }[],
    custom: Record<string, { x: number; y: number }>
  ): Record<string, { x: number; y: number }> => {
    const used = new Set<string>();
    const out: Record<string, { x: number; y: number }> = {};
    icons.forEach((def, i) => {
      const d = custom[def.windowId] ?? {
        x: ICON_ORIGIN_X + (i % 4) * ICON_STEP_X,
        y: Math.floor(i / 4) * ICON_STEP_Y,
      };
      let gx = Math.max(0, Math.round((d.x - ICON_ORIGIN_X) / ICON_STEP_X) * ICON_STEP_X + ICON_ORIGIN_X);
      let gy = Math.max(0, Math.round(d.y / ICON_STEP_Y) * ICON_STEP_Y);
      let key = gx + ',' + gy;
      let guard = 0;
      while (used.has(key) && guard < 500) {
        gx += ICON_STEP_X;
        key = gx + ',' + gy;
        guard++;
      }
      used.add(key);
      out[def.windowId] = { x: gx, y: gy };
    });
    return out;
  };

  // 桌面图标自定义位置（拖拽后持久化到 localStorage，key=windowId）
  const [iconPositions, setIconPositions] = useState<Record<string, { x: number; y: number }>>(() => {
    try {
      const raw = localStorage.getItem('lumi_desktop_icon_positions');
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });

  const saveIconPosition = useCallback((windowId: string, x: number, y: number) => {
    setIconPositions(prev => {
      const next = { ...prev, [windowId]: { x: Math.round(x), y: Math.round(y) } };
      try { localStorage.setItem('lumi_desktop_icon_positions', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const resetIconPositions = useCallback(() => {
    setIconPositions({});
    try { localStorage.removeItem('lumi_desktop_icon_positions'); } catch {}
  }, []);

  // 标记刚发生过拖拽，用于拖拽结束后抑制误触发的 click（打开窗口）
  const iconDraggedRef = useRef(false);

  // Desktop icon layout: absolute positioning, 4 columns, fixed spacing
  const desktopIcons = [
    ...(user?.role === 'admin' ? [{ id: 'admin-center', labelKey: 'adminCenter', icon: <Shield size={24} />, colorClass: 'from-red-500 to-rose-600', windowId: 'admin-center' }] : []),
    { id: 'runtime-log', labelKey: 'runtimeLog', icon: <TerminalIcon size={24} />, colorClass: 'from-teal-500 to-cyan-600', windowId: 'runtime-log' },
    { id: 'tools', labelKey: 'tools', icon: <Wrench size={24} />, colorClass: 'from-amber-500 to-orange-600', windowId: 'tools' },
    { id: 'skills', labelKey: 'skills', icon: <Sparkles size={24} />, colorClass: 'from-emerald-500 to-teal-600', windowId: 'skills' },
    { id: 'team', labelKey: 'team', icon: <Bot size={24} />, colorClass: 'from-cyan-500 to-blue-600', windowId: 'team' },
    { id: 'memory-avatar', labelKey: 'memoryAvatars', icon: <Castle size={24} />, colorClass: 'from-fuchsia-500 to-purple-600', windowId: 'memory-avatar' },
    { id: 'avatar-studio', labelKey: 'avatarStudio', icon: <Brush size={24} />, colorClass: 'from-cyan-400 to-blue-600', windowId: 'avatar-studio' },
    { id: 'sound', labelKey: 'sound', icon: <Volume2 size={24} />, colorClass: 'from-sky-500 to-indigo-600', windowId: 'sound' },
    { id: 'music', labelKey: 'music', icon: <Music size={24} />, colorClass: 'from-red-500 to-pink-600', windowId: 'music-center' },
    { id: 'growth', labelKey: 'growth', icon: <TrendingUp size={24} />, colorClass: 'from-rose-500 to-pink-600', windowId: 'growth' },
    { id: 'workflow', labelKey: 'workflow', icon: <Workflow size={24} />, colorClass: 'from-violet-500 to-purple-600', windowId: 'workflow' },
    { id: 'file-explorer', labelKey: 'fileManager', icon: <FolderOpen size={24} />, colorClass: 'from-lime-500 to-green-600', windowId: 'file-explorer' },
    { id: 'calendar', labelKey: 'calendar', icon: <Calendar size={24} />, colorClass: 'from-blue-500 to-indigo-600', windowId: 'calendar' },
    { id: 'screenshot', labelKey: 'screenshot', icon: <Camera size={24} />, colorClass: 'from-orange-500 to-red-600', windowId: 'screenshot' },
    { id: 'stats', labelKey: 'stats', icon: <BarChart3 size={24} />, colorClass: 'from-cyan-500 to-teal-600', windowId: 'stats' },
  ];

  // 解析后的去重叠坐标（渲染与拖拽均以此为准）
  const resolvedIconPositions = resolveDesktopIconPositions(desktopIcons, iconPositions);

  const handleWallpaperUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // v3.12.11 P1-7: Canvas 压缩后再存 localStorage，避免 base64 超 5MB
    const img = new Image();
    img.onload = () => {
      const MAX_DIM = 1920;
      let w = img.naturalWidth, h = img.naturalHeight;
      if (w > MAX_DIM || h > MAX_DIM) {
        const ratio = Math.min(MAX_DIM / w, MAX_DIM / h);
        w = Math.round(w * ratio);
        h = Math.round(h * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, w, h);
      const url = canvas.toDataURL('image/jpeg', 0.7);

      setWallpaperUrl(url);
      setWallpaper('custom');
      try {
        localStorage.setItem('xf_wallpaper_type', 'custom');
        localStorage.setItem('xf_wallpaper_url', url);
      } catch (lsErr) {
        console.warn('[Wallpaper] localStorage 超限，壁纸不持久化:', lsErr);
      }
    };
    img.src = URL.createObjectURL(file);
  };

  const handleWindowMinimize = async () => {
    try {
      const { invoke } = await import('@tauri-apps/api/core');
      await invoke('minimize_window');
    } catch {}
  };
  const handleWindowMaximize = async () => {
    try {
      const { invoke } = await import('@tauri-apps/api/core');
      await invoke('toggle_maximize_window');
    } catch {}
  };
  const handleWindowClose = async () => {
    try {
      const { invoke } = await import('@tauri-apps/api/core');
      await invoke('close_window');
    } catch {}
  };

  const [isTrainingOpen, setIsTrainingOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(() => {
    try { return typeof window !== 'undefined' && localStorage.getItem('xf_onboarding_seen') !== 'true'; } catch { return false; }
  });
  const [sensorPrimerSeen, setSensorPrimerSeen] = useState(() => {
    try { return typeof window !== 'undefined' && localStorage.getItem('xf_sensor_primer_seen') === 'true'; } catch { return false; }
  });
  const finishSensorPrimer = useCallback(() => {
    localStorage.setItem('xf_sensor_primer_seen', 'true');
    setSensorPrimerSeen(true);
  }, []);
  const [showTooltips, setShowTooltips] = useState(() => {
    try {
      const seen = localStorage.getItem('xf_context_tooltips_seen');
      if (!seen) return false;
      return Date.now() < parseInt(seen, 10);
    } catch { return false; }
  });
  const [quickReviewMode, setQuickReviewMode] = useState(false);
  const [onboardingStartStep, setOnboardingStartStep] = useState(0);
  const [mcpActivities, setMcpActivities] = useState<Array<{
    id: string; device: string; action: string; status: string;
    message?: string; title?: string; path?: string; slidesCount?: number; toolCalls?: number; error?: string;
    time: number;
  }>>([]);
  const timerRefs = useRef<Record<string, ReturnType<typeof setTimeout> | undefined>>({});
  const [showMcpPanel, setShowMcpPanel] = useState(false);
  const [agentStatus, setAgentStatus] = useState<'idle' | 'thinking' | 'background' | 'executing' | 'waiting_confirmation' | 'done' | 'error'>('idle');
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStep[]>([]);
  const [backgroundWorkflowTasks, setBackgroundWorkflowTasks] = useState<BackgroundWorkflowTask[]>([]);
  const [pendingOperationMode, setPendingOperationMode] = useState<OperationMode | null>(null);
  const seenWorkflowToolEvents = useRef<Set<string>>(new Set());
  const backgroundTaskStatusRef = useRef<Map<string, string>>(new Map());
  const [meetingNotesOpen, setMeetingNotesOpen] = useState(false);
  const [meetingStartedAt, setMeetingStartedAt] = useState<number | null>(() => {
    const saved = localStorage.getItem('xf_meeting_started_at');
    return saved ? Number(saved) || null : null;
  });
  const [meetingNotes, setMeetingNotes] = useState<MeetingNote[]>(() => {
    try { return JSON.parse(localStorage.getItem('xf_meeting_notes') || '[]'); } catch { return []; }
  });
  const [meetingReport, setMeetingReport] = useState<string>(() => localStorage.getItem('xf_meeting_report') || '');
  const [meetingReportGenerating, setMeetingReportGenerating] = useState(false);
  const [legalMeetingCaseTitle, setLegalMeetingCaseTitle] = useState(() => getLegalCaseLabel(getLegalConsultationCase()));
  const meetingModeRef = useRef(operationMode === 'meeting');
  const meetingVoiceActiveRef = useRef(false);
  const lastMeetingTranscriptRef = useRef<{ text: string; at: number }>({ text: '', at: 0 });
  const lastLegalMeetingArchiveRef = useRef('');
  useEffect(() => {
    meetingModeRef.current = operationMode === 'meeting';
  }, [operationMode]);

  const persistMeetingNotes = useCallback((notes: MeetingNote[]) => {
    localStorage.setItem('xf_meeting_notes', JSON.stringify(notes.slice(-300)));
  }, []);

  const resetMeetingCapture = useCallback((startedAt = Date.now()) => {
    setMeetingNotes([]);
    setMeetingReport('');
    setMeetingStartedAt(startedAt);
    localStorage.setItem('xf_meeting_notes', '[]');
    localStorage.removeItem('xf_meeting_report');
    localStorage.setItem('xf_meeting_started_at', String(startedAt));
    lastMeetingTranscriptRef.current = { text: '', at: 0 };
  }, []);

  const appendMeetingTranscript = useCallback((text: string, isFinal: boolean) => {
    if (!meetingModeRef.current || !isFinal) return;
    const clean = text.trim();
    if (!clean) return;
    const now = Date.now();
    if (lastMeetingTranscriptRef.current.text === clean && now - lastMeetingTranscriptRef.current.at < 4000) return;
    lastMeetingTranscriptRef.current = { text: clean, at: now };
    setMeetingReport('');
    localStorage.removeItem('xf_meeting_report');
    setMeetingStartedAt(prev => {
      if (prev) return prev;
      localStorage.setItem('xf_meeting_started_at', String(now));
      return now;
    });
    setMeetingNotes(prev => {
      const next = [...prev, { id: `${now}-${crypto.randomUUID()}`, text: clean, time: now }];
      persistMeetingNotes(next);
      return next;
    });
  }, [persistMeetingNotes]);

  const socket = useSocket();
  const musicVisible = useMusicVisible();
  const musicSnapshot = useMusicPlayerSnapshot();
  const voiceprint = useVoiceprint({ socket });
  const ownerVoiceGateOpen = useCallback(() => {
    // v3.12.63: 主动点击语音按钮 = 用户显式意愿，应放行不再卡 templates/声纹
    // 1) 未注册声纹的用户直接放行（绝大多数）
    if (voiceprint.enrolledCount === 0) return true;
    // 2) templates 还在加载中：先放行，避免 fetch 慢/失败导致用户永远开不了语音
    if (!voiceprint.templatesLoaded) return true;
    // 3) 有模板但置信度不足：放行（用户在主动点按钮，信任手动操作）
    if (!voiceprint.hasUsableTemplates) return true;
    // 4) 全部就绪时仍要求声纹匹配（仅作为辅助安全）
    return voiceprint.result.isOwnerSpeaking && voiceprint.result.confidence >= 0.68;
  }, [
    voiceprint.enrolledCount,
    voiceprint.hasUsableTemplates,
    voiceprint.result.confidence,
    voiceprint.result.isOwnerSpeaking,
    voiceprint.templatesLoaded,
  ]);
  useAmbientPoller(socket); // Ambient awareness: polls window, clipboard, idle state
  const { callState, audioLevel, startCall, startCallRef, endCall, error: callError, transcript, interrupt, toggleMute, isMuted, switchPersonality } = useVoiceCall({
    socket,
    onTranscript: appendMeetingTranscript,
    canInterruptFromVoice: ownerVoiceGateOpen,
    canSendMicAudio: ownerVoiceGateOpen,
  });
  // v3.12.5: systemStats 给 DesktopDock 状态指示灯用（cpuPercent/memoryPercent 实时数据）
  const { stats: rawSystemStats } = useSystemStats();
  const systemStats = useMemo(() => rawSystemStats ? {
    cpuPercent: rawSystemStats.cpu_percent,
    memoryPercent: rawSystemStats.memory_percent,
  } : undefined, [rawSystemStats]);
  const voiceScopeOptions = useMemo(() => (
    workDomain === 'work' && orgConnection?.connected && orgConnection?.orgId
      ? { domain: 'work' as const, orgId: orgConnection.orgId }
      : { domain: 'personal' as const }
  ), [orgConnection?.connected, orgConnection?.orgId, workDomain]);
  const getVoiceScopeOptions = useCallback(() => voiceScopeOptions, [voiceScopeOptions]);
  useEffect(() => {
    void voiceprint.loadTemplates();
  }, [voiceprint.loadTemplates]);
  useEffect(() => {
    if (!voiceprint.templatesLoaded || voiceprint.enrolledCount === 0 || !voiceprint.hasUsableTemplates) return;
    void voiceprint.startListening();
    return () => voiceprint.stopListening();
  }, [
    voiceprint.enrolledCount,
    voiceprint.hasUsableTemplates,
    voiceprint.startListening,
    voiceprint.stopListening,
    voiceprint.templatesLoaded,
  ]);
  const meetingStartAttemptRef = useRef(0);

  const startStandardVoiceCall = useCallback(() => {
    void startCall(selectedVoiceId, activePersonality, activePersonality, getVoiceScopeOptions());
  }, [activePersonality, getVoiceScopeOptions, selectedVoiceId, startCall]);

  const stopMeetingAudio = useCallback(() => {
    meetingVoiceActiveRef.current = false;
    if (operationMode === 'meeting') setOperationMode('assistant');
    if (callState !== 'idle') endCall();
  }, [callState, endCall, operationMode, setOperationMode]);

  useEffect(() => {
    if (operationMode !== 'meeting') {
      if (meetingVoiceActiveRef.current && callState !== 'idle') {
        meetingVoiceActiveRef.current = false;
        endCall();
      }
      return;
    }

    setMeetingStartedAt(prev => {
      if (prev) return prev;
      const now = Date.now();
      localStorage.setItem('xf_meeting_started_at', String(now));
      return now;
    });

    if (callState === 'idle') {
      const now = Date.now();
      if (now - meetingStartAttemptRef.current < 3000) return;
      meetingStartAttemptRef.current = now;
      meetingVoiceActiveRef.current = true;
      void startCall(selectedVoiceId, activePersonality, activePersonality, { ...getVoiceScopeOptions(), transcriptionOnly: true });
    }
  }, [activePersonality, callState, endCall, getVoiceScopeOptions, operationMode, selectedVoiceId, startCall]);
  // Spacebar push-to-talk: track whether this call was started by spacebar
  const isSpacebarRecording = useRef(false);
  const callStateRef = useRef(callState);
  useEffect(() => { callStateRef.current = callState; }, [callState]);
  const runtimeLogOpenRef = useRef(openWindows.includes('runtime-log'));
  useEffect(() => { runtimeLogOpenRef.current = openWindows.includes('runtime-log'); }, [openWindows]);
  // Wake word detection — server-side Qwen ASR (DASHSCOPE_API_KEY), falls back to Picovoice
  // Default off — user must explicitly enable in Settings to avoid continuous ASR charges
  const [wake启用d, setWake启用d] = useState(() => localStorage.getItem('xf_wake_word_enabled') === 'true');
  useEffect(() => {
    const syncWakeSetting = () => {
      setWake启用d(localStorage.getItem('xf_wake_word_enabled') === 'true');
    };
    const onSettingChanged = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.key === 'xf_wake_word_enabled') setWake启用d(detail.value === true || detail.value === 'true');
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === 'xf_wake_word_enabled') syncWakeSetting();
    };
    window.addEventListener('xf:setting-changed', onSettingChanged);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('xf:setting-changed', onSettingChanged);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  useEffect(() => {
    let disposed = false;

    const refreshPermissions = async () => {
      const snapshot = await getSensorPermissionSnapshot({
        desktopAutomation: isTauri ? 'available' : 'unavailable',
        wakeWordEnabled: wake启用d,
        sensorPrimerSeen,
        biometricsPrimerSeen: sensorPrimerSeen,
      });
      if (disposed) return;
      setClientPermissions({ ...snapshot });
    };

    void refreshPermissions();
    const onSensorChange = () => void refreshPermissions();
    window.addEventListener(SENSOR_PERMISSIONS_CHANGED, onSensorChange);
    window.addEventListener('visibilitychange', onSensorChange);
    const interval = window.setInterval(refreshPermissions, 30000);
    return () => {
      disposed = true;
      window.removeEventListener(SENSOR_PERMISSIONS_CHANGED, onSensorChange);
      window.removeEventListener('visibilitychange', onSensorChange);
      window.clearInterval(interval);
    };
  }, [isTauri, sensorPrimerSeen, wake启用d]);

  useEffect(() => {
    if (!isTauri) {
      setClientRuntime({ lastError: 'Native runtime unavailable outside desktop client' });
      return;
    }
    let disposed = false;
    const refreshRuntime = async () => {
      try {
        const { invoke } = await import('@tauri-apps/api/core');
        const status: any = await invoke('get_runtime_resilience_status');
        if (disposed) return;
        setClientRuntime({
          autostartSupported: Boolean(status.autostart_supported),
          autostart启用d: Boolean(status.autostart_enabled),
          closeToBackground: Boolean(status.close_to_background),
          startedInBackground: Boolean(status.started_in_background),
          backendNodeRunning: Boolean(status.backend_node_running),
          backendPythonRunning: Boolean(status.backend_python_running),
          nodeRestarts: Number(status.node_restarts || 0),
          pythonRestarts: Number(status.python_restarts || 0),
          globalShortcut: String(status.global_shortcut || 'Alt+空格'),
          lastError: '',
        });
      } catch (err: any) {
        if (disposed) return;
        setClientRuntime({ lastError: err?.message || '无法获取原生运行时状态' });
      }
    };
    void refreshRuntime();
    const interval = window.setInterval(refreshRuntime, 30000);
    return () => {
      disposed = true;
      window.clearInterval(interval);
    };
  }, [isTauri]);

  useEffect(() => {
    if (!isTauri || closeToBackgroundSyncRef.current) return;
    closeToBackgroundSyncRef.current = true;
    const syncClosePreference = async () => {
      try {
        const { invoke } = await import('@tauri-apps/api/core');
        const status: any = await invoke('get_runtime_resilience_status');
        const saved = localStorage.getItem('xf_close_to_background');
        if (status?.started_in_background) {
          localStorage.setItem('xf_close_to_background', 'true');
          await invoke('set_close_to_background', { enabled: true });
        } else if (saved === 'true' || saved === 'false') {
          await invoke('set_close_to_background', { enabled: saved === 'true' });
        }
      } catch {}
    };
    void syncClosePreference();
  }, [isTauri]);

  const wakeWord = useWakeWord({
    socket,
    startCallRef,
    enabled: wake启用d,
    keyword: '小枫',
    voiceId: selectedVoiceId,
    personalityId: 'xiaofeng',
    agentId: 'xiaofeng',
    startCallOptions: voiceScopeOptions,
    onDetection: () => sounds.playWakeChime(),
    canAcceptWake: ownerVoiceGateOpen,
    canSendWakeAudio: ownerVoiceGateOpen,
    isCallActive: () => callState !== 'idle',
    onInterrupt: () => interrupt(),
  });

  // ── Biometrics: voiceprint + face recognition + presence ──
  const faceRecognition = useFaceRecognition({ enabled: sensorPrimerSeen, socket });
  const presence = usePresence({
    socket,
    faceResult: faceRecognition.result,
    voiceprintResult: voiceprint.result,
    userId: user?.uid,
  });

  // Idle→active return greeting — listens for ambient idle reports and fires on return
  const lastIdleRef = useRef<number>(0);
  const greetedRef = useRef(false);
  const IDLE_AWAY_S = 5 * 60; // 5 min considered "away"
  const RETURN_S = 30;        // < 30s considered "back"
  useEffect(() => {
    if (!socket) return;
    const onIdleReport = (data: { idle_ms: number; idle_seconds: number }) => {
      const idleS = data.idle_seconds ?? (data.idle_ms / 1000);
      const wasAway = lastIdleRef.current > IDLE_AWAY_S;
      const isBack = idleS < RETURN_S;
      const allowProactiveGreeting = localStorage.getItem('xf_allow_proactive_voice') === 'true';
      if (wasAway && isBack && !greetedRef.current && allowProactiveGreeting) {
        greetedRef.current = true;
        // LLM-generated personalized greeting — server generates, TTS speaks
        socket.emit('greeting:generate', { scene: 'return' });
      }
      if (idleS >= IDLE_AWAY_S) {
        greetedRef.current = false;
      }
      lastIdleRef.current = idleS;
    };
    socket.on('ambient:idle_echo', onIdleReport);
    return () => { socket.off('ambient:idle_echo', onIdleReport); };
  }, [socket]);

  useEffect(() => {
    if (callError) toast.error(callError);
  }, [callError]);

  const formatMeetingTime = useCallback((value: number) => {
    return new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }, []);

  const buildMeetingMarkdown = useCallback(() => {
    const started = meetingStartedAt ? new Date(meetingStartedAt) : new Date();
    const lines = [
      `# 小枫 Meeting Notes`,
      '',
      `Started: ${started.toLocaleString()}`,
      ...(legalMeetingCaseTitle ? [`Case: ${legalMeetingCaseTitle}`] : []),
      '',
      ...(meetingReport ? ['## 小枫 Report', '', meetingReport, ''] : []),
      '## Transcript',
      '',
      ...meetingNotes.map(note => `- [${formatMeetingTime(note.time)}] ${note.text}`),
      '',
    ];
    return lines.join('\n');
  }, [formatMeetingTime, legalMeetingCaseTitle, meetingNotes, meetingReport, meetingStartedAt]);

  const buildFallbackMeetingReport = useCallback(() => {
    const started = meetingStartedAt ? new Date(meetingStartedAt).toLocaleString() : new Date().toLocaleString();
    const legalCase = getLegalConsultationCase();
    const actionHints = meetingNotes
      .filter(note => /(todo|action|next|follow|owner|deadline|需要|安排|确认|推进|负责|下周|明天|今天|完成|决定|风险|问题|证据|材料|开庭|上诉|法院|法官)/i.test(note.text))
      .slice(-8)
      .map(note => `- [${formatMeetingTime(note.time)}] ${note.text}`);
    if (legalCase) {
      return [
        lang === 'zh' ? '# 小枫 律所会谈纪要' : '# 小枫 Legal Consultation Memo',
        '',
        `${lang === 'zh' ? '案件' : 'Case'}: ${getLegalCaseLabel(legalCase)}`,
        `${lang === 'zh' ? '开始时间' : 'Started'}: ${started}`,
        `${lang === 'zh' ? '记录条数' : 'Transcript items'}: ${meetingNotes.length}`,
        '',
        `## ${lang === 'zh' ? '会谈纪要' : 'Consultation Summary'}`,
        meetingNotes.length > 0
          ? (lang === 'zh' ? `本次会谈共收录 ${meetingNotes.length} 条转写。LLM 分析暂不可用，以下为本地基础整理。` : `Captured ${meetingNotes.length} transcript items. LLM analysis was unavailable; this is a local structured memo.`)
          : (lang === 'zh' ? '本次会谈没有收录到可整理的转写。' : 'No transcript was captured for this consultation.'),
        '',
        `## ${lang === 'zh' ? '事实摘要' : 'Fact Summary'}`,
        ...(meetingNotes.slice(-6).map(note => `- ${note.text}`)),
        ...(meetingNotes.length === 0 ? [`- ${lang === 'zh' ? '暂无事实摘要。' : 'No fact summary yet.'}`] : []),
        '',
        `## ${lang === 'zh' ? '争议焦点' : 'Issues'}`,
        `- ${lang === 'zh' ? '请律师结合案由、证据和对方主张进一步确认。' : 'Counsel should confirm issues against claims, evidence, and procedural posture.'}`,
        '',
        `## ${lang === 'zh' ? '待补材料' : 'Missing Materials'}`,
        ...(actionHints.length > 0 ? actionHints : [`- ${lang === 'zh' ? '暂未检测到明确待补材料。' : 'No clear missing materials detected.'}`]),
        '',
        `## ${lang === 'zh' ? '下一步建议' : 'Next Steps'}`,
        `- ${lang === 'zh' ? '复核会谈转写，补充证据清单、责任人和期限。' : 'Review the transcript and add evidence list, owners, and deadlines.'}`,
        '',
        `## ${lang === 'zh' ? '安全边界' : 'Safety Boundary'}`,
        `- ${lang === 'zh' ? '本纪要仅辅助律师分析，最终法律意见和对外文书由执业律师确认。' : 'This memo assists legal analysis only; final legal advice and filings require licensed counsel review.'}`,
      ].join('\n');
    }
    return [
      lang === 'zh' ? '# 小枫 会议报告' : '# 小枫 Meeting Report',
      '',
      `${lang === 'zh' ? '开始时间' : 'Started'}: ${started}`,
      `${lang === 'zh' ? '记录条数' : 'Transcript items'}: ${meetingNotes.length}`,
      '',
      `## ${lang === 'zh' ? '会议摘要' : 'Summary'}`,
      meetingNotes.length > 0
        ? (lang === 'zh' ? `本次会议共收录 ${meetingNotes.length} 条转写。LLM 分析暂不可用，下面是基于转写的基础整理。` : `Captured ${meetingNotes.length} transcript items. LLM analysis was unavailable, so this is a basic local report.`)
        : (lang === 'zh' ? '本次会议没有可整理的转写内容。' : 'No transcript was captured for this meeting.'),
      '',
      `## ${lang === 'zh' ? '待办/决策线索' : 'Action / Decision Signals'}`,
      ...(actionHints.length > 0 ? actionHints : [`- ${lang === 'zh' ? '未检测到明确待办或决策线索。' : 'No clear action or decision signals detected.'}`]),
      '',
      `## ${lang === 'zh' ? '建议' : 'Suggestion'}`,
      `- ${lang === 'zh' ? '建议人工复核转写，补充负责人、截止时间和最终决策。' : 'Review the transcript manually and add owners, deadlines, and final decisions.'}`,
    ].join('\n');
  }, [formatMeetingTime, lang, meetingNotes, meetingStartedAt]);

  const analyzeMeetingNotes = useCallback(async (endedAt = Date.now()) => {
    if (meetingNotes.length === 0) {
      const fallback = buildFallbackMeetingReport();
      setMeetingReport(fallback);
      localStorage.setItem('xf_meeting_report', fallback);
      toast.info(lang === 'zh' ? '会议没有收录到转写，已生成空会议报告' : 'No transcript captured; generated an empty meeting report');
      return fallback;
    }

    setMeetingReportGenerating(true);
    try {
      const legalCase = getLegalConsultationCase();
      const res = await apiFetch('/api/meeting/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          provider: aiConfig?.provider || 'Gemini',
          model: aiConfig?.model,
          notes: meetingNotes,
          startedAt: meetingStartedAt,
          endedAt,
          language: lang,
          purpose: legalCase ? 'legal_consultation' : 'meeting',
          legalCase: legalCase ? {
            title: legalCase.title,
            caseNumber: legalCase.caseNumber,
            party: legalCase.party,
            cause: legalCase.cause,
            court: legalCase.court,
            judge: legalCase.judge,
            stage: legalCase.stage,
            notes: legalCase.notes,
          } : undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || (lang === 'zh' ? '会议分析失败' : 'Meeting analysis failed'));
      const report = String(data.report || '').trim() || buildFallbackMeetingReport();
      setMeetingReport(report);
      localStorage.setItem('xf_meeting_report', report);
      toast.success(lang === 'zh' ? '小枫 已整理会议报告' : '小枫 generated the meeting report');
      return report;
    } catch (err: any) {
      const fallback = buildFallbackMeetingReport();
      setMeetingReport(fallback);
      localStorage.setItem('xf_meeting_report', fallback);
      toast.error(err?.message || (lang === 'zh' ? '会议分析失败，已生成基础报告' : 'Meeting analysis failed; generated a basic report'));
      return fallback;
    } finally {
      setMeetingReportGenerating(false);
    }
  }, [aiConfig?.model, aiConfig?.provider, buildFallbackMeetingReport, lang, meetingNotes, meetingStartedAt]);

  const archiveLegalMeetingReport = useCallback(async (report: string, endedAt: number) => {
    const consultationCaseId = getLegalConsultationCaseId();
    if (!consultationCaseId || meetingNotes.length === 0) return;
    const lastNote = meetingNotes[meetingNotes.length - 1];
    const archiveKey = `${consultationCaseId}:${meetingStartedAt || ''}:${lastNote?.id || meetingNotes.length}`;
    if (lastLegalMeetingArchiveRef.current === archiveKey) return;

    if (workDomain === 'work' && orgConnection?.connected) {
      const started = meetingStartedAt ? new Date(meetingStartedAt) : new Date(endedAt);
      const transcript = meetingNotes
        .map(note => `- [${formatMeetingTime(note.time)}] ${note.text}`)
        .join('\n');
      const content = [
        `# 当事人会谈 ${started.toLocaleString()}`,
        '',
        '## 小枫 会谈整理',
        '',
        report,
        '',
        '## 原始转写',
        '',
        transcript,
        '',
        '## 安全边界',
        '',
        '本记录用于辅助律师分析，最终法律意见与对外文书由执业律师确认。',
      ].join('\n');
      try {
        const res = await apiFetch(`/api/org/legal/cases/${encodeURIComponent(consultationCaseId)}/materials`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            type: 'consultation',
            title: `当事人会谈 ${started.toLocaleString()}`,
            content,
            source: 'meeting',
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || (lang === 'zh' ? '会谈归档失败' : 'Consultation archive failed'));
        lastLegalMeetingArchiveRef.current = archiveKey;
        clearLegalConsultationCaseId();
        setLegalMeetingCaseTitle('');
        window.dispatchEvent(new CustomEvent('xf:org-legal-cases-changed'));
        toast.success(lang === 'zh' ? '会谈已归档到组织案件' : 'Consultation archived to organization case');
        return;
      } catch (err: any) {
        toast.error(err?.message || (lang === 'zh' ? '会谈归档到组织案件失败' : 'Failed to archive consultation to organization case'));
        return;
      }
    }

    const archived = archiveLegalMeetingToConsultationCase({
      report,
      notes: meetingNotes,
      startedAt: meetingStartedAt,
      endedAt,
    });
    if (!archived) {
      toast.error(lang === 'zh' ? '会谈归档失败，请检查当前案件' : 'Failed to archive consultation to the case');
      return;
    }
    lastLegalMeetingArchiveRef.current = archiveKey;
    setLegalMeetingCaseTitle('');
    toast.success(lang === 'zh' ? `会谈已归档到案件：${getLegalCaseLabel(archived.caseFile)}` : `Consultation archived to case: ${getLegalCaseLabel(archived.caseFile)}`);
  }, [formatMeetingTime, lang, meetingNotes, meetingStartedAt, orgConnection?.connected, workDomain]);

  const endMeetingAndReport = useCallback(async () => {
    const endedAt = Date.now();
    stopMeetingAudio();
    setMeetingNotesOpen(true);
    const report = await analyzeMeetingNotes(endedAt);
    await archiveLegalMeetingReport(report, endedAt);
  }, [analyzeMeetingNotes, archiveLegalMeetingReport, stopMeetingAudio]);

  const endVoiceCallFromUI = useCallback(() => {
    if (operationMode === 'meeting') {
      void endMeetingAndReport();
      return;
    }
    endCall();
  }, [endCall, endMeetingAndReport, operationMode]);

  const copyMeetingNotes = useCallback(async () => {
    if (meetingNotes.length === 0) {
      toast.info(lang === 'zh' ? '暂无会议笔记' : 'No meeting notes yet');
      return;
    }
    try {
      await navigator.clipboard.writeText(buildMeetingMarkdown());
      toast.success(lang === 'zh' ? '会议笔记已复制' : 'Meeting notes copied');
    } catch (err: any) {
      toast.error(err?.message || (lang === 'zh' ? '复制失败' : 'Failed to copy notes'));
    }
  }, [buildMeetingMarkdown, lang, meetingNotes.length]);

  const downloadMeetingNotes = useCallback(() => {
    if (meetingNotes.length === 0) {
      toast.info(lang === 'zh' ? '暂无会议笔记' : 'No meeting notes yet');
      return;
    }
    const blob = new Blob([buildMeetingMarkdown()], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    const stamp = new Date(meetingStartedAt || Date.now()).toISOString().replace(/[:.]/g, '-');
    anchor.href = url;
    anchor.download = `xiaofeng-meeting-${stamp}.md`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
    toast.success(lang === 'zh' ? '会议笔记已导出' : 'Meeting notes exported');
  }, [buildMeetingMarkdown, lang, meetingNotes.length, meetingStartedAt]);

  const clearMeetingNotes = useCallback(() => {
    const now = Date.now();
    setMeetingNotes([]);
    setMeetingReport('');
    setMeetingStartedAt(now);
    localStorage.setItem('xf_meeting_notes', '[]');
    localStorage.removeItem('xf_meeting_report');
    localStorage.setItem('xf_meeting_started_at', String(now));
    lastMeetingTranscriptRef.current = { text: '', at: 0 };
    lastLegalMeetingArchiveRef.current = '';
    toast.success(lang === 'zh' ? '会议笔记已清空' : 'Meeting notes cleared');
  }, [lang]);

  const requestOperationModeChange = useCallback((nextMode: OperationMode) => {
    if (nextMode === operationMode) return;
    if (nextMode === 'meeting' || nextMode === 'autonomous') {
      setPendingOperationMode(nextMode);
      return;
    }
    setOperationMode(nextMode);
  }, [operationMode, setOperationMode]);

  const confirmOperationModeChange = useCallback(() => {
    if (!pendingOperationMode) return;
    setOperationMode(pendingOperationMode);
    if (pendingOperationMode === 'meeting') setMeetingNotesOpen(true);
    setPendingOperationMode(null);
  }, [pendingOperationMode, setOperationMode]);

  const openMeetingMode = useCallback(() => {
    if (operationMode === 'meeting') {
      setMeetingNotesOpen(true);
      return;
    }
    requestOperationModeChange('meeting');
  }, [operationMode, requestOperationModeChange]);

  useEffect(() => {
    const handler = () => openMeetingMode();
    window.addEventListener('xf:request-meeting-mode', handler);
    return () => window.removeEventListener('xf:request-meeting-mode', handler);
  }, [openMeetingMode]);

  // Listen for org navigation events
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.tab === 'home') {
        setOpenWindows([]);
        setFocusedWindow(null);
        setWindowOrder([]);
        setKnowledgeOpen(false);
        setChatOpen(false);
        setActiveTab('home');
        return;
      }
      if (detail?.tab) {
        // Anyone can open the org tab — join/create/connect handled by OrgPortal
        setActiveTab(detail.tab);
        // P1-9 fix: sync window state when activeTab changes via external event
        if (detail.tab !== 'home' && detail.tab !== 'knowledge' && detail.tab !== 'chat') {
          setOpenWindows(prev => prev.includes(detail.tab!) ? prev : [...prev, detail.tab!]);
          setFocusedWindow(detail.tab!);
          setWindowOrder(prev => prev.includes(detail.tab!) ? prev : [...prev, detail.tab!]);
          setKnowledgeOpen(false);
          setChatOpen(false);
        }
      }
    };
    window.addEventListener('xf:navigate', handler);
    return () => window.removeEventListener('xf:navigate', handler);
  }, [setActiveTab]);

  // Listen for Memory 形象 Lab open request from AgentGenerator
  useEffect(() => {
    const handler = () => openMemory形象();
    window.addEventListener('xf:open-memory-lab', handler);
    return () => window.removeEventListener('xf:open-memory-lab', handler);
  }, []);

  // Restore real system volume/brightness on mount
  useEffect(() => {
    systemService.getVolume().then(v => setVolume(v)).catch(() => {});
    systemService.getBrightness().then(b => setBrightness(b)).catch(() => {});
  }, []);

  const applyWallpaperMode = useCallback((enabled: boolean, options: { silent?: boolean; timeoutMs?: number } = {}) => {
    if (wallpaperAutomationTimerRef.current) {
      clearTimeout(wallpaperAutomationTimerRef.current);
      wallpaperAutomationTimerRef.current = null;
    }

    setIsWallpaperMode(enabled);
    void systemService.setWallpaperMode(enabled);

    if (enabled && options.timeoutMs) {
      wallpaperAutomationTimerRef.current = setTimeout(() => {
        if (!wallpaperWas启用dBeforeAutomationRef.current) {
          setIsWallpaperMode(false);
          void systemService.setWallpaperMode(false);
        }
        wallpaperWas启用dBeforeAutomationRef.current = false;
        wallpaperAutomationTimerRef.current = null;
        toast(t.wallpaperAutoRestored || '桌面控制超时后已恢复壁纸模式', {
          icon: <Box className="text-white/40" />,
        });
      }, Math.max(15_000, options.timeoutMs));
    }

    if (!options.silent) {
      toast(enabled ? (t.wallpaperFusionActive || '壁纸融合已激活') : (t.standardFocusMode || '标准桌面'), {
        icon: enabled ? <Sparkles className="text-celestial-saturn" /> : <Box className="text-white/40" />
      });
    }
  }, [t]);

  const toggleWallpaperMode = useCallback(() => {
    applyWallpaperMode(!isWallpaperMode);
  }, [applyWallpaperMode, isWallpaperMode]);

  const dismissWallpaperWorkPrompt = useCallback(() => {
    if (wallpaperWorkPromptTimerRef.current) {
      clearTimeout(wallpaperWorkPromptTimerRef.current);
      wallpaperWorkPromptTimerRef.current = null;
    }
    setWallpaperWorkPromptVisible(false);
  }, []);

  const showWallpaperWorkPrompt = useCallback(() => {
    if (isWallpaperModeRef.current || chatOpenRef.current) return;
    setWallpaperWorkPromptVisible(true);
    if (wallpaperWorkPromptTimerRef.current) {
      clearTimeout(wallpaperWorkPromptTimerRef.current);
    }
    wallpaperWorkPromptTimerRef.current = setTimeout(() => {
      setWallpaperWorkPromptVisible(false);
      wallpaperWorkPromptTimerRef.current = null;
    }, 14000);
  }, []);

  const enterWallpaperFromWorkPrompt = useCallback(() => {
    dismissWallpaperWorkPrompt();
    applyWallpaperMode(true);
  }, [applyWallpaperMode, dismissWallpaperWorkPrompt]);

  useEffect(() => {
    return () => {
      if (wallpaperWorkPromptTimerRef.current) {
        clearTimeout(wallpaperWorkPromptTimerRef.current);
        wallpaperWorkPromptTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ enabled?: boolean; timeoutMs?: number }>).detail || {};
      const enabled = Boolean(detail.enabled);
      if (enabled) {
        wallpaperWas启用dBeforeAutomationRef.current = isWallpaperModeRef.current;
      } else if (wallpaperWas启用dBeforeAutomationRef.current) {
        if (wallpaperAutomationTimerRef.current) {
          clearTimeout(wallpaperAutomationTimerRef.current);
          wallpaperAutomationTimerRef.current = null;
        }
        wallpaperWas启用dBeforeAutomationRef.current = false;
        return;
      }

      applyWallpaperMode(enabled, {
        silent: true,
        timeoutMs: enabled ? detail.timeoutMs : undefined,
      });
      if (!enabled) wallpaperWas启用dBeforeAutomationRef.current = false;
    };
    window.addEventListener('xf:set-wallpaper-mode', handler);
    return () => {
      window.removeEventListener('xf:set-wallpaper-mode', handler);
      if (wallpaperAutomationTimerRef.current) {
        clearTimeout(wallpaperAutomationTimerRef.current);
        wallpaperAutomationTimerRef.current = null;
      }
    };
  }, [applyWallpaperMode]);


  // MCP Live Activity socket listener
  useEffect(() => {
    if (!socket) return;
    const handler = (data: any) => {
      const activity = { ...data, id: Date.now().toString(), time: Date.now() };
      setMcpActivities(prev => [activity, ...prev].slice(0, 20));
      setShowMcpPanel(true);
      const autoHideTimer = setTimeout(() => {
        setMcpActivities(prev => {
          if (prev.length === 0 || Date.now() - prev[0].time > 8000) setShowMcpPanel(false);
          return prev;
        });
      }, 8000);
      timerRefs.current.mcpActivity = autoHideTimer;
    };
    socket.on('mcp:activity', handler);
    return () => { socket.off('mcp:activity', handler); clearTimeout(timerRefs.current.mcpActivity); };
  }, [socket]);

  const upsertBackgroundWorkflowTask = useCallback((task: BackgroundWorkflowTask) => {
    if (!task?.id) return;
    setBackgroundWorkflowTasks(prev => {
      const existing = prev.findIndex(item => item.id === task.id);
      const nextTask = { ...prev[existing], ...task };
      const next = existing >= 0
        ? prev.map(item => item.id === task.id ? nextTask : item)
        : [nextTask, ...prev];
      return next.slice(0, 6);
    });

    if (['completed', 'failed', 'cancelled'].includes(task.status)) {
      window.setTimeout(() => {
        setBackgroundWorkflowTasks(prev => prev.filter(item => item.id !== task.id));
        backgroundTaskStatusRef.current.delete(task.id);
      }, 12000);
    }
  }, []);

  const cancelBackgroundWorkflowTask = useCallback((taskId: string) => {
    socket?.emit('agent:background_cancel', { taskId });
    setBackgroundWorkflowTasks(prev => prev.map(task =>
      task.id === taskId ? { ...task, status: 'cancelling' } : task
    ));
  }, [socket]);

  useEffect(() => {
    apiFetch('/api/autonomy/background-tasks', { credentials: 'include' })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (!Array.isArray(data?.tasks)) return;
        data.tasks.slice(0, 6).forEach((task: BackgroundWorkflowTask) => upsertBackgroundWorkflowTask(task));
      })
      .catch(() => {});
  }, [upsertBackgroundWorkflowTask]);

  // Workflow status listener — agent:status, agent:tool_call, agent:response, agent:error
  useEffect(() => {
    if (!socket) return;

    const onStatus = (data: { status: string; agentName?: string; phase?: string; detail?: string; source?: string }) => {
      if (data.status === 'thinking') {
        const isBackground = data.phase === 'background';
        if (isBackground && data.source !== 'chat') showWallpaperWorkPrompt();
        setAgentStatus(isBackground ? 'background' : 'thinking');
        setWorkflowSteps(prev => [...prev, {
          id: `thinking-${Date.now()}`,
          type: isBackground ? 'background' : 'thinking',
          text: isBackground
            ? (t.workflowBackgroundStep || '小枫 is handling this in the background')
            : (t.workflowAnalyzing || '正在分析你的请求...'),
          detail: data.detail || (data.agentName && data.agentName !==  '小枫'  ? data.agentName : undefined),
          time: Date.now(),
        }]);
      } else if (data.status === 'idle') {
        setAgentStatus('done');
        setWorkflowSteps(prev => [...prev, {
          id: `done-${Date.now()}`,
          type: 'response',
          text: t.workflowCompleted || '已完成',
          time: Date.now(),
        }]);
        setTimeout(() => {
          setAgentStatus('idle');
          setWorkflowSteps([]);
        }, 5000);
      } else if (data.status === 'error') {
        setAgentStatus('error');
        setTimeout(() => {
          setAgentStatus('idle');
          setWorkflowSteps([]);
        }, 5000);
      }
    };

    const onToolCall = (data: { correlationId?: string; name: string; arguments?: any; args?: any; result?: string; error?: string; source?: string }) => {
      const toolArgs = data.arguments ?? data.args;
      const phase = data.error !== undefined ? 'error' : data.result !== undefined ? 'result' : 'start';
      if (data.correlationId) {
        const eventKey = `${data.correlationId}:${phase}`;
        if (seenWorkflowToolEvents.current.has(eventKey)) return;
        seenWorkflowToolEvents.current.add(eventKey);
      }
      if (data.source !== 'chat') showWallpaperWorkPrompt();
      if (data.result !== undefined) {
        setAgentStatus('executing');
        triggerPetReaction('jump', 1200);
        setWorkflowSteps(prev => [...prev, {
          id: `tool-ok-${Date.now()}-${crypto.randomUUID()}`,
          type: 'tool_result',
          text: `${data.name} ${t.workflowToolDone || '已完成'}`,
          detail: data.result?.slice(0, 100),
          time: Date.now(),
        }]);
      } else if (data.error !== undefined) {
        setAgentStatus('executing');
        triggerPetReaction('failed', 2000);
        setWorkflowSteps(prev => [...prev, {
          id: `tool-err-${Date.now()}-${crypto.randomUUID()}`,
          type: 'error',
          text: `${data.name} ${t.workflowToolFailed || '失败'}`,
          detail: data.error?.slice(0, 100),
          time: Date.now(),
        }]);
      } else {
        setAgentStatus('executing');
        const argsSummary = toolArgs
          ? Object.entries(toolArgs).map(([k, v]) => `${k}=${typeof v === 'string' ? v.slice(0, 30) : String(v).slice(0, 30)}`).join(', ')
          : '';
        setWorkflowSteps(prev => [...prev, {
          id: `tool-start-${Date.now()}-${crypto.randomUUID()}`,
          type: 'tool_start',
          text: `${t.workflowCalling || '正在调用'} ${data.name}`,
          detail: argsSummary || undefined,
          time: Date.now(),
        }]);
      }
    };

    const onConfirmTool = (data: { correlationId: string; name: string; arguments?: any; source?: string }) => {
      if (data.source !== 'chat') showWallpaperWorkPrompt();
      setAgentStatus('waiting_confirmation');
      const argsSummary = data.arguments
        ? Object.entries(data.arguments).map(([k, v]) => `${k}=${typeof v === 'string' ? v.slice(0, 30) : String(v).slice(0, 30)}`).join(', ')
        : '';
      setWorkflowSteps(prev => [...prev, {
        id: `confirm-${data.correlationId || Date.now()}`,
        type: 'confirmation',
        text: `${t.workflowWaitingConfirm || '等待批准'}: ${data.name}`,
        detail: argsSummary || (t.workflowConfirmHint || '请在权限对话框中确认以继续。'),
        time: Date.now(),
      }]);
    };

    const onResponse = (data: { text: string; agentName?: string }) => {
      setWorkflowSteps(prev => [...prev, {
        id: `resp-${Date.now()}`,
        type: 'response',
        text: t.workflowResponseReady || '响应就绪',
        detail: data.text?.slice(0, 100),
        time: Date.now(),
      }]);
    };

    const onError = (data: { message: string }) => {
      setAgentStatus('error');
      setWorkflowSteps(prev => [...prev, {
        id: `err-${Date.now()}`,
        type: 'error',
        text: t.workflowError || '处理失败',
        detail: data.message,
        time: Date.now(),
      }]);
      setTimeout(() => {
        setAgentStatus('idle');
        setWorkflowSteps([]);
      }, 5000);
    };

    const onProactive = (data: { type?: string; taskId: string; message: string; timestamp: string }) => {
      const taskId = data.type || data.taskId || data.taskId;
      if (taskId === 'greeting' && localStorage.getItem('xf_allow_proactive_voice') !== 'true') return;
      // Always add to notification center so user can find it later
      addNotification({
        type: taskId === 'daily_summary' || taskId === 'evening_wrapup' ? 'success' :
              taskId === 'memory_decay' || taskId === 'reminder_check' ? 'warning' : 'info',
        title: taskId === 'daily_summary' ? 'Daily Summary' :
               taskId === 'evening_wrapup' ? 'Evening Wrap-up' :
               taskId === 'reminder_check' ? 'Reminder' :
               taskId === 'memory_decay' ? 'Memory' :
               taskId === 'behavioral_analysis' ? 'Insight' : '小枫',
        message: data.message,
      });
      // Trigger pet reaction
      switch (taskId) {
        case 'reminder_check': triggerPetReaction('wave', 2000); break;
        case 'daily_summary': triggerPetReaction('wave', 2000); break;
        case 'evening_wrapup': triggerPetReaction('wave', 2000); break;
        case 'memory_decay': triggerPetReaction('jump', 1500); break;
        case 'behavioral_analysis': triggerPetReaction('jump', 1500); break;
        default: triggerPetReaction('jump', 1200); break;
      }
    };

    const normalizeBackgroundTask = (data: any): BackgroundWorkflowTask | null => {
      const raw = data?.task || data;
      const id = String(raw?.id || data?.taskId || '');
      if (!id) return null;
      const workerNames = Array.isArray(raw?.workerNames)
        ? raw.workerNames
        : Array.isArray(raw?.workers)
          ? raw.workers.map((worker: any) => worker?.name || worker?.id).filter(Boolean)
          : [];
      return {
        id,
        title: raw?.title || data?.title || id,
        status: (raw?.status || 'queued') as BackgroundWorkflowTask['status'],
        workerNames,
        toolCallsCount: Number(raw?.toolCallsCount || 0),
        error: raw?.error,
        resultPreview: raw?.resultPreview,
        updatedAt: raw?.updatedAt,
      };
    };

    const recordBackgroundTaskStep = (task: BackgroundWorkflowTask) => {
      const previousStatus = backgroundTaskStatusRef.current.get(task.id);
      if (previousStatus === task.status) return;
      backgroundTaskStatusRef.current.set(task.id, task.status);
      const isActive = task.status === 'queued' || task.status === 'running' || task.status === 'cancelling';
      const isFailed = task.status === 'failed';
      setAgentStatus(isActive ? 'background' : isFailed ? 'error' : 'done');
      if (isActive) showWallpaperWorkPrompt();
      setWorkflowSteps(prev => [...prev, {
        id: `background-task-${task.id}-${task.status}-${Date.now()}`,
        type: isFailed ? 'error' : task.status === 'completed' ? 'response' : 'background',
        text: `${t.workflowBackgroundTask || '后台任务'}: ${task.status}`,
        detail: task.title || task.id,
        time: Date.now(),
      }]);
    };

    const onDelegation = (data: any) => {
      const task = normalizeBackgroundTask(data);
      if (!task) return;
      upsertBackgroundWorkflowTask(task);
      recordBackgroundTaskStep(task);
    };

    const onBackgroundTaskUpdate = (data: any) => {
      const task = normalizeBackgroundTask(data);
      if (!task) return;
      upsertBackgroundWorkflowTask(task);
      recordBackgroundTaskStep(task);
    };

    socket.on('agent:status', onStatus);
    socket.on('agent:delegation', onDelegation);
    socket.on('agent:background_task_update', onBackgroundTaskUpdate);
    socket.on('agent:tool_call', onToolCall);
    socket.on('agent:tool', onToolCall);
    socket.on('agent:confirm_tool', onConfirmTool);
    socket.on('agent:response', onResponse);
    socket.on('agent:error', onError);
    socket.on('agent:proactive', onProactive);
    const onPreferencesChanged = (data: { key: string; value: any }) => {
      if (petPrefsSavingRef.current) return; // ignore our own changes
      if (data.key === 'pet' && data.value) {
        const { pet, accessories } = data.value;
        if (pet) {
          const resolved = resolvePetPreference(pet);
          if (resolved) {
            setSelectedPet(resolved);
            localStorage.setItem('xf_selected_pet', JSON.stringify(serializePetPreference(resolved)));
          }
        } else {
          setSelectedPet(null);
          localStorage.removeItem('xf_selected_pet');
        }
        if (accessories) {
          setEquippedAccessories(accessories);
          localStorage.setItem('xf_accessories', JSON.stringify(accessories));
        }
        toast.info(lang === 'zh' ? '桌面形象已从另一设备同步' : 'Desktop avatar synced from another device');
      }
    };
    const onAgentPromoted = (data: { agentName: string; skillName?: string }) => {
      const msg = data.skillName
        ? `Agent "${data.agentName}" auto-promoted with skill "${data.skillName}"`
        : `Agent "${data.agentName}" has been auto-created`;
      addNotification({ type: 'system', title: 'Agent Promoted', message: msg });
      toast.info(msg, { duration: 5000 });
    };
    const onAgentNotification = (data: { type: string; level: string; message: string }) => {
      addNotification({ type: data.level === 'critical' ? 'warning' : data.level === 'warning' ? 'warning' : 'info', title: data.type || '小枫', message: data.message });
      if (data.level === 'critical') {
        toast.error(data.message, { duration: 10000 });
      } else if (data.level === 'warning') {
        toast.warning(data.message, { duration: 5000 });
      } else {
        toast(data.message, { duration: 5000 });
      }
    };

    const onWakeDetected = (data: { keyword: string }) => {
      addNotification({
        type: 'info',
        title: lang === 'zh' ? '唤醒词检测' : 'Wake Word Detected',
        message: lang === 'zh' ? `检测到唤醒词 "${data.keyword}"` : `Detected wake word "${data.keyword}"`,
      });
    };
    const onWakeError = (data: { message: string }) => {
      console.warn('[Wake] Error:', data.message);
    };
    const onWakeStarted = () => {
      const feeMsg = lang === 'zh'
        ? '语音唤醒服务已启动，持续监听按秒计费约 0.00033 元/秒（约 1.2 元/小时），请合理使用以免产生高额费用。'
        : 'Voice wake service started. Continuous ASR billing at ~0.00033 RMB/sec (~1.2 RMB/hr). Use reasonably to avoid high charges.';
      addNotification({
        type: 'info',
        title: lang === 'zh' ? '语音唤醒' : 'Voice Wake',
        message: feeMsg,
      });
    };

    const onTokenUsageUpdate = (_data: { totalTokens: number; provider: string }) => {
      // Token usage updated — TokenDashboard handles REST polling, this is real-time supplement
    };
    const onTokenQuotaUpdate = (data: { used: number; cap: number; remaining: number }) => {
      const pct = data.used / data.cap;
      if (pct >= 0.9) {
        addNotification({
          type: 'warning',
          title: lang === 'zh' ? 'Token 配额告警' : 'Token Quota Alert',
          message: lang === 'zh'
            ? `已使用 ${Math.round(pct * 100)}%（${data.used.toLocaleString()} / ${data.cap.toLocaleString()}）`
            : `${Math.round(pct * 100)}% used (${data.used.toLocaleString()} / ${data.cap.toLocaleString()})`,
        });
      }
    };

    socket.on('preferences:changed', onPreferencesChanged);
    socket.on('agent:promoted', onAgentPromoted);
    socket.on('agent:notification', onAgentNotification);
    socket.on('wake:detected', onWakeDetected);
    socket.on('wake:error', onWakeError);
    socket.on('wake:started', onWakeStarted);
    socket.on('token:usage_update', onTokenUsageUpdate);
    socket.on('token:quota_update', onTokenQuotaUpdate);

    return () => {
      socket.off('agent:status', onStatus);
      socket.off('agent:delegation', onDelegation);
      socket.off('agent:background_task_update', onBackgroundTaskUpdate);
      socket.off('agent:tool_call', onToolCall);
      socket.off('agent:tool', onToolCall);
      socket.off('agent:confirm_tool', onConfirmTool);
      socket.off('agent:response', onResponse);
      socket.off('agent:error', onError);
      socket.off('agent:proactive', onProactive);
      socket.off('preferences:changed', onPreferencesChanged);
      socket.off('agent:promoted', onAgentPromoted);
      socket.off('agent:notification', onAgentNotification);
      socket.off('wake:detected', onWakeDetected);
      socket.off('wake:error', onWakeError);
      socket.off('wake:started', onWakeStarted);
      socket.off('token:usage_update', onTokenUsageUpdate);
      socket.off('token:quota_update', onTokenQuotaUpdate);
    };
  }, [socket]);

  // Fetch pet preferences from server on mount (cross-device sync source of truth)
  useEffect(() => {
    const fetchPrefs = async () => {
      try {
        const res = await apiFetch('/api/preferences/pet');
        if (res.ok) {
          const data = await res.json();
          if (data.pet) {
            const resolved = resolvePetPreference(data.pet);
            if (resolved) {
              setSelectedPet(resolved);
              localStorage.setItem('xf_selected_pet', JSON.stringify(serializePetPreference(resolved)));
            }
          }
          if (data.accessories?.length > 0) {
            setEquippedAccessories(data.accessories);
            localStorage.setItem('xf_accessories', JSON.stringify(data.accessories));
          }
        }
      } catch {}
    };
    fetchPrefs();
  }, []);

  useEffect(() => {
    const isInputFocused = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = (el as HTMLElement).tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || (el as HTMLElement).isContentEditable;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setHelpOpen(true);
        return;
      }
      if (e.key === 'Escape') {
        setIsSearchOpen(false);
        setIsControlCenterOpen(false);
        if (isWallpaperMode) toggleWallpaperMode();
        return;
      }
      if (e.key === ' ' && !e.repeat) {
        if (isInputFocused()) return;
        if (runtimeLogOpenRef.current || isSearchOpen || isControlCenterOpen) return;
        if (meetingModeRef.current) return;
        e.preventDefault();
        const cs = callStateRef.current;
        if (cs === 'speaking') {
          interrupt();
          startCall(selectedVoiceId, 'xiaofeng', 'xiaofeng', getVoiceScopeOptions());
          isSpacebarRecording.current = true;
        } else if (cs === 'idle') {
          startCall(selectedVoiceId, 'xiaofeng', 'xiaofeng', getVoiceScopeOptions());
          isSpacebarRecording.current = true;
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === ' ' && isSpacebarRecording.current) {
        isSpacebarRecording.current = false;
        endCall();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [endCall, getVoiceScopeOptions, interrupt, isControlCenterOpen, isSearchOpen, isWallpaperMode, selectedVoiceId, startCall, toggleWallpaperMode]);

  const [bootVisible, setBootVisible] = useState(() => {
    try {
      if (sessionStorage.getItem('xf_skip_boot') === '1') {
        sessionStorage.removeItem('xf_skip_boot');
        return false;
      }
    } catch (e) { /* ignore */ }
    return true;
  });

  // 断连状态浮层：socket 掉线时提示重连，不整页刷新（A2）
  const [socketStatus, setSocketStatus] = useState<'connected' | 'disconnected' | 'reconnecting'>('connected');
  const [hasEverConnected, setHasEverConnected] = useState(false);
  useEffect(() => {
    const off = socketService.onStatus((st) => {
      if (st === 'connected') setHasEverConnected(true);
      setSocketStatus(st);
    });
    return off;
  }, []);

  // 启动后水合用户设置（跟随账号）：拉取服务端并同步到本地 + 派发事件
  useEffect(() => {
    fetchUserSettings().catch(() => {});
  }, []);

  // Remove the old interval-based boot logic since HardcoreBootSequence handles it

  // v3.12.10: time 已由 ClockContext 管理，不再需要 setInterval

  const handleSelectPet = (pet: PetConfig) => {
    setSelectedPet(pet);
    savePetPrefsToServer(pet, equippedAccessories);
    toast.info(`${pet.name} ${t.avatarSetAsDesktop || '已设为桌面形象'}`);
  };

  const openMemory形象 = async () => {
    try { sounds.playClick(); } catch {}
    try {
      const res = await apiFetch('/api/agents/sanctuaries');
      if (res.ok) {
        const data = await res.json();
        if (data.agents && data.agents.length > 0) {
          setSanctuaryAgent(data.agents[0]);
          setSanctuaryOpen(true);
          return;
        }
      }
    } catch {}
    setMemoryLabOpen(true);
  };

  const toggleWindow = (tab: string) => {
    try { sounds.playClick(); } catch {}
    if (tab === 'home') {
      setOpenWindows([]);
      setFocusedWindow(null);
      setActiveTab('home');
      return;
    }
    if (tab === 'org') {
      setActiveTab('org');
      return;
    }
    if (tab === 'memory') {
      setKnowledgeOpen(true);
      setActiveTab('knowledge');
      return;
    }
    if (tab === 'sync') {
      tab = 'devices';
    }
    if (tab === 'notifications') {
      setIsNotificationPanelOpen(prev => !prev);
      setOpenWindows(prev => prev.filter(w => w !== 'notifications'));
      setMinimizedWindows(prev => prev.filter(w => w !== 'notifications'));
      setWindowOrder(prev => prev.filter(w => w !== 'notifications'));
      if (focusedWindow === 'notifications') setFocusedWindow(null);
      return;
    }

    // Knowledge base and Chat open fullscreen, not as windows
    if (tab === 'knowledge') {
      setKnowledgeOpen(prev => !prev);
      return;
    }
    if (tab === 'chat') {
      setChatOpen(prev => !prev);
      setActiveTab(tab);
      return;
    }
    if (tab === 'memory-avatar') {
      openMemory形象();
      return;
    }
    if (tab === 'avatar-studio') {
      // Opens as a normal window below
    }

    if (openWindows.includes(tab)) {
      if (minimizedWindows.includes(tab)) {
        setMinimizedWindows(prev => prev.filter(w => w !== tab));
      }
      setFocusedWindow(tab);
      setWindowOrder(prev => [...prev.filter(w => w !== tab), tab]);
    } else {
      setOpenWindows([...openWindows, tab]);
      setFocusedWindow(tab);
      setWindowOrder(prev => [...prev, tab]);
    }
    setActiveTab(tab);
  };

  const closeWindow = (tab: string) => {
    try { sounds.playClick(); } catch {}
    const nextWindows = openWindows.filter(w => w !== tab);
    setOpenWindows(nextWindows);
    setMinimizedWindows(prev => prev.filter(w => w !== tab));
    setWindowOrder(prev => prev.filter(w => w !== tab));
    if (focusedWindow === tab) {
      setFocusedWindow(nextWindows.length > 0 ? nextWindows[nextWindows.length - 1] : null);
      if (nextWindows.length === 0) setActiveTab('home');
    }
  };

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<any>).detail || {};
      const action = String(detail.action || '');
      const target = String(detail.target || '');
      const mode = String(detail.mode || '');
      const task = String(detail.task || '');
      const section = String(detail.section || '');
      const confirmed = Boolean(detail.confirmed);
      const respond = typeof detail.respond === 'function' ? detail.respond : () => {};
      const reject = typeof detail.reject === 'function' ? detail.reject : () => {};

      const normalizeTarget = (value: string) => {
        if (value === 'music') return 'music-center';
        if (value === 'memory') return 'knowledge';
        if (value === 'files') return 'knowledge';
        if (value === 'sync') return 'devices';
        return value;
      };

      const openSurface = (value: string) => {
        const windowId = normalizeTarget(value);
        if (!windowId) throw new Error('Client action requires a target surface');

        if (windowId === 'home') {
          setOpenWindows([]);
          setFocusedWindow(null);
          setWindowOrder([]);
          setActiveTab('home');
          return;
        }
        if (windowId === 'org') {
          setActiveTab('org');
          return;
        }
        if (windowId === 'knowledge') {
          setKnowledgeOpen(true);
          setActiveTab('knowledge');
          return;
        }
        if (windowId === 'chat') {
          setChatOpen(true);
          setActiveTab('chat');
          return;
        }
        if (windowId === 'notifications') {
          setIsNotificationPanelOpen(true);
          setOpenWindows(prev => prev.filter(w => w !== 'notifications'));
          setMinimizedWindows(prev => prev.filter(w => w !== 'notifications'));
          setWindowOrder(prev => prev.filter(w => w !== 'notifications'));
          if (focusedWindow === 'notifications') setFocusedWindow(null);
          return;
        }
        if (windowId === 'memory-avatar') {
          void openMemory形象();
          return;
        }
        setOpenWindows(prev => prev.includes(windowId) ? prev : [...prev, windowId]);
        setMinimizedWindows(prev => prev.filter(w => w !== windowId));
        setFocusedWindow(windowId);
        setWindowOrder(prev => [...prev.filter(w => w !== windowId), windowId]);
        setActiveTab(windowId);
      };

      const closeSurface = (value: string) => {
        const windowId = normalizeTarget(value);
        if (!windowId) throw new Error('close_app requires target');
        if (windowId === 'knowledge') {
          setKnowledgeOpen(false);
          return;
        }
        if (windowId === 'chat') {
          setChatOpen(false);
          return;
        }
        if (windowId === 'notifications') {
          setIsNotificationPanelOpen(false);
          return;
        }
        if (windowId === 'org' && activeTab === 'org') {
          setActiveTab('home');
          return;
        }
        closeWindow(windowId);
      };

      const setClientMode = (value: string) => {
        if (value === 'music') {
          openSurface('music-center');
          return;
        }
        const allowed = ['chat', 'meeting', 'assistant', 'autonomous'];
        if (!allowed.includes(value)) throw new Error(`Unsupported mode: ${value}`);
        if ((value === 'autonomous' || value === 'meeting') && !confirmed) {
          throw new Error(`${value} mode requires explicit user confirmation`);
        }
        setOperationMode(value as OperationMode);
        if (value === 'meeting') setMeetingNotesOpen(true);
      };

      try {
        if (action === 'refresh_client_state') {
          window.dispatchEvent(new CustomEvent('xf:client-state-refresh'));
          respond({ ok: true, action, mode: operationMode, activeTab, openWindows });
          return;
        }
        if (action === 'open_app') {
          openSurface(target);
          respond({ ok: true, action, target });
          return;
        }
        if (action === 'close_app') {
          closeSurface(target);
          respond({ ok: true, action, target });
          return;
        }
        if (action === 'set_mode' || action === 'set_client_mode') {
          setClientMode(mode);
          respond({ ok: true, action, mode });
          return;
        }
        if (action === 'focus_home') {
          openSurface('home');
          respond({ ok: true, action });
          return;
        }
        if (action === 'open_music_center') {
          openSurface('music-center');
          respond({ ok: true, action, target: 'music-center', mode: operationMode });
          return;
        }
        if (action === 'show_music_layer' || action === 'hide_music_layer') {
          if (action === 'show_music_layer') {
            if (!musicSnapshot.track) {
              openSurface('music-center');
              respond({ ok: false, action, mode: operationMode, reason: 'music_track_required', target: 'music-center' });
              return;
            }
          }
          window.dispatchEvent(new CustomEvent('xf:music-layer', { detail: { visible: action === 'show_music_layer' } }));
          respond({ ok: true, action, mode: operationMode });
          return;
        }
        if (action === 'start_meeting_mode') {
          if (!confirmed) throw new Error('start_meeting_mode requires explicit user confirmation');
          if (detail.resetNotes) resetMeetingCapture();
          if (detail.legalCaseTitle) setLegalMeetingCaseTitle(String(detail.legalCaseTitle));
          else if (!getLegalConsultationCaseId()) setLegalMeetingCaseTitle('');
          setClientMode('meeting');
          respond({ ok: true, action, mode: 'meeting' });
          return;
        }
        if (action === 'end_meeting_mode') {
          if (!confirmed) throw new Error('end_meeting_mode requires explicit user confirmation');
          void endMeetingAndReport();
          respond({ ok: true, action, status: 'ending_and_generating_report' });
          return;
        }
        if (action === 'open_meeting_notes') {
          setMeetingNotesOpen(true);
          respond({ ok: true, action });
          return;
        }
        if (action === 'open_runtime_log') {
          openSurface('runtime-log');
          respond({ ok: true, action, target: 'runtime-log' });
          return;
        }
        if (action === 'show_knowledge_base') {
          openSurface('knowledge');
          respond({ ok: true, action, target: 'knowledge' });
          return;
        }
        if (action === 'open_organization_workspace') {
          openSurface('org');
          respond({ ok: true, action, target: 'org' });
          return;
        }
        if (action === 'open_files') {
          openSurface('knowledge');
          respond({ ok: true, action, target: 'knowledge' });
          return;
        }
        if (action === 'open_settings') {
          if (section === 'computer') {
            openSurface('kernel');
            respond({ ok: true, action, target: 'kernel' });
            return;
          }
          if (section) setSettingsSection(section);
          openSurface('settings');
          respond({ ok: true, action, target: 'settings', section });
          return;
        }
        if (action === 'open_computer_adaptation') {
          openSurface('kernel');
          respond({ ok: true, action, target: 'kernel' });
          return;
        }
        if (action === 'open_plans' || action === 'open_work_queue') {
          openSurface('plans');
          respond({ ok: true, action, target: 'plans' });
          return;
        }
        if (action === 'open_avatar_studio') {
          openSurface('avatar-studio');
          respond({ ok: true, action, target: 'avatar-studio' });
          return;
        }
        if (action === 'open_sound_studio') {
          openSurface('sound');
          respond({ ok: true, action, target: 'sound' });
          return;
        }
        if (action === 'open_memory_avatar') {
          openSurface('memory-avatar');
          respond({ ok: true, action, target: 'memory-avatar' });
          return;
        }
        if (action === 'open_skills' || action === 'open_tools' || action === 'open_team' || action === 'open_chat') {
          const mapped = action === 'open_skills'
            ? 'skills'
            : action === 'open_tools'
              ? 'tools'
              : action === 'open_team'
                ? 'team'
                : 'chat';
          openSurface(mapped);
          respond({ ok: true, action, target: mapped });
          return;
        }
        if (action === 'set_wallpaper_mode') {
          const enabled = Boolean(detail.enabled);
          if (enabled && !confirmed) throw new Error('set_wallpaper_mode requires explicit user confirmation');
          applyWallpaperMode(enabled);
          respond({ ok: true, action, enabled });
          return;
        }
        throw new Error(`Unsupported client action: ${action}`);
      } catch (err: any) {
        reject(err?.message || String(err));
      }
    };

    window.addEventListener('xf:client-action', handler);
    return () => window.removeEventListener('xf:client-action', handler);
  }, [
    activeTab,
    applyWallpaperMode,
    closeWindow,
    endMeetingAndReport,
    musicSnapshot.track,
    resetMeetingCapture,
    setActiveTab,
    setOperationMode,
  ]);

  useEffect(() => {
    if (!socket) return;
    const sendState = () => {
      const recentErrors = [
        callError ? { source: 'voice', message: callError, at: Date.now() } : null,
        musicSnapshot.lastError ? { source: 'music', message: musicSnapshot.lastError, at: Date.now() } : null,
        clientRuntime.lastError ? { source: 'runtime', message: clientRuntime.lastError, at: Date.now() } : null,
      ].filter(Boolean);

      socket.emit('client:state', {
        platform: isTauri ? 'desktop' : 'web',
        mode: operationMode,
        activeTab,
        workDomain,
        org: {
          connected: Boolean(orgConnection?.connected),
          id: orgConnection?.orgId || '',
          name: orgConnection?.orgName || '',
          role: orgConnection?.orgRole || '',
        },
        windows: {
          open: openWindows,
          focused: focusedWindow,
          minimized: minimizedWindows,
        },
        surfaces: {
          knowledgeOpen,
          chatOpen,
          runtimeLogOpen: openWindows.includes('runtime-log'),
          meetingOpen: meetingNotesOpen,
          musicLayerVisible: musicVisible,
          wallpaperMode: isWallpaperMode,
        },
        voice: {
          state: callState,
          muted: isMuted,
        },
        music: {
          visible: musicSnapshot.visible,
          isPlaying: musicSnapshot.isPlaying,
          trackName: musicSnapshot.track?.name || '',
          artists: musicSnapshot.track?.artists || [],
          album: musicSnapshot.track?.album || '',
          source: musicSnapshot.source,
          progress: musicSnapshot.progress,
          duration: musicSnapshot.duration,
          volume: musicSnapshot.volume,
          mood: musicSnapshot.mood,
          hasLyrics: musicSnapshot.lyrics.length > 0,
          layerVisible: musicVisible,
          lastError: musicSnapshot.lastError || '',
        },
        meeting: {
          active: operationMode === 'meeting',
          noteCount: meetingNotes.length,
          hasReport: Boolean(meetingReport),
          startedAt: meetingStartedAt,
          reportGenerating: meetingReportGenerating,
        },
        runtimeLog: {
          open: openWindows.includes('runtime-log'),
          status: clientRuntime.lastError ? 'attention' : 'ready',
          lastError: clientRuntime.lastError || '',
        },
        permissions: clientPermissions,
        tools: {
          agentStatus,
          workflowStepCount: workflowSteps.length,
          runningWorkflowSteps: workflowSteps.filter(step =>
            step.type === 'thinking' ||
            step.type === 'background' ||
            step.type === 'confirmation' ||
            step.type === 'tool_start'
          ).length,
          mcpActivityCount: mcpActivities.length,
        },
        runtime: clientRuntime,
        errors: recentErrors,
      });
    };
    sendState();
    const interval = setInterval(sendState, 10000);
    window.addEventListener('xf:client-state-refresh', sendState);
    return () => {
      clearInterval(interval);
      window.removeEventListener('xf:client-state-refresh', sendState);
    };
  }, [
    activeTab,
    callState,
    chatOpen,
    clientPermissions,
    clientRuntime,
    callError,
    focusedWindow,
    agentStatus,
    isMuted,
    isTauri,
    isWallpaperMode,
    knowledgeOpen,
    mcpActivities.length,
    meetingNotes.length,
    meetingNotesOpen,
    meetingReport,
    meetingReportGenerating,
    meetingStartedAt,
    minimizedWindows,
    musicSnapshot,
    musicVisible,
    openWindows,
    operationMode,
    orgConnection?.connected,
    orgConnection?.orgName,
    orgConnection?.orgRole,
    socket,
    workDomain,
    workflowSteps,
  ]);

  const handleContextAction = (action: string, context: any) => {
    switch (action) {
      case 'refresh':
        window.location.reload();
        break;
      case 'change_wallpaper':
        wallpaperInputRef.current?.click();
        break;
      case 'reset_wallpaper':
        setWallpaper('celestial');
        setWallpaperUrl('');
        localStorage.removeItem('xf_wallpaper_type');
        localStorage.removeItem('xf_wallpaper_url');
        break;
      case 'display_settings':
        toggleWindow('settings');
        setSettingsSection('general');
        break;
      case 'open_terminal':
        toggleWindow('terminal');
        break;
      case 'open':
        if (context?.targetId === 'files') {
          setKnowledgeOpen(true);
          setActiveTab('knowledge');
        }
        else if (context?.targetId) toggleWindow(context.targetId);
        break;
      case 'properties': {
        const meta = getWindowMeta(context?.targetId || '');
        toast.info(lang === 'zh'
          ? `${meta.label}\n类型：应用窗口\n标识：${context?.targetId || '—'}`
          : `${meta.label}\nType: App Window\nID: ${context?.targetId || '—'}`);
        break;
      }
    }
  };

  const { menu, menuItems: contextItems, showMenu: showContextMenu, execute: executeContextMenu } = useContextMenu();

  const appIcons = [
    { id: 'chat', label: t.chat || '聊天', icon: <MessageSquare size={24} />, color: 'from-green-500 to-emerald-600' },
    { id: 'personality', label: t.personality || '人格实验室', icon: <UserIcon size={24} />, color: 'from-violet-500 to-fuchsia-600' },
    { id: 'kernel', label: t.kernelMonitor || '内核监视器', icon: <Activity size={24} />, color: 'from-orange-500 to-red-600' },
    { id: 'devices', label: t.devices || '设备', icon: <Cpu size={24} />, color: 'from-blue-600 to-cyan-400' },
    { id: 'settings', label: t.settings || '系统完整', icon: <SettingsIcon size={24} />, color: 'from-gray-400 to-slate-600' },
  ];

  const desktopAppEntries = desktopIcons.map(def => ({
    id: def.windowId,
    label: (t as any)[def.labelKey] || def.labelKey,
    icon: def.icon,
    color: def.colorClass,
  }));
  const utilityAppEntries = [
    { id: 'knowledge', label: t.knowledgeBase || '知识库', icon: <BrainCircuit size={24} />, color: 'from-cyan-400 to-blue-600' },
    { id: 'notifications', label: t.notificationsLabel || '通知', icon: <Bell size={24} />, color: 'from-amber-500 to-orange-600' },
    { id: 'terminal', label: t.terminal || '终端', icon: <TerminalIcon size={24} />, color: 'from-green-500 to-emerald-600' },
    { id: 'voice', label: t.voiceLabel || '语音', icon: <Volume2 size={24} />, color: 'from-pink-500 to-rose-600' },
    { id: 'memory', label: t.memory || '记忆', icon: <BrainCircuit size={24} />, color: 'from-cyan-500 to-blue-600' },
    { id: 'mcp', label: t.mcp || 'MCP', icon: <Wrench size={24} />, color: 'from-purple-500 to-violet-600' },
    { id: 'sync', label: t.sync || '同步', icon: <RefreshCw size={24} />, color: 'from-blue-500 to-indigo-600' },
    { id: 'reminders', label: t.reminders || '提醒', icon: <Calendar size={24} />, color: 'from-amber-500 to-orange-600' },
    { id: 'plans', label: t.plans || '计划', icon: <Calendar size={24} />, color: 'from-celestial-saturn to-orange-600' },
    { id: 'tokens', label: t.tokens || '令牌', icon: <Circle size={24} />, color: 'from-celestial-mars to-celestial-saturn' },
    { id: 'profile', label: t.profile || '个人资料', icon: <UserIcon size={24} />, color: 'from-white/30 to-white/10' },
  ];
  const allAppEntries = [...appIcons, ...desktopAppEntries, ...utilityAppEntries]
    .filter((entry, index, list) => list.findIndex(other => other.id === entry.id) === index);
  const getWindowMeta = (windowId: string) => allAppEntries.find(entry => entry.id === windowId) || {
    id: windowId,
    label: windowId,
    icon: <Circle size={24} />,
    color: 'from-celestial-mars to-celestial-saturn',
  };

  const sphereSentiment =
    openWindows.includes('kernel') ? 'excited' :
    chatOpen ? 'focused' : 'default';

  const getWindowSize = (windowId: string) => {
    if (windowId === 'admin-center') return { w: '1100px', h: '780px' };
    if (windowId === 'settings') return { w: '1050px', h: '720px' };
    if (windowId === 'knowledge') return { w: '1100px', h: '750px' };
    if (windowId === 'kernel') return { w: '1050px', h: '720px' };
    if (windowId === 'personality') return { w: '1050px', h: '720px' };
    if (windowId === 'generate') return { w: '1050px', h: '720px' };
    if (windowId === 'music') return { w: '1050px', h: '720px' };
    if (windowId === 'music-center') return { w: '800px', h: '600px' };
    if (windowId === 'tools') return { w: '850px', h: '620px' };
    if (windowId === 'team') return { w: '900px', h: '700px' };
    if (windowId === 'github-mcp') return { w: '850px', h: '620px' };
    if (windowId === 'notifications') return { w: '700px', h: '550px' };
    if (windowId === 'reminders') return { w: '650px', h: '620px' };
    if (windowId === 'plans') return { w: '980px', h: '700px' };
    if (windowId === 'devices') return { w: '900px', h: '700px' };
    if (windowId === 'tokens') return { w: '800px', h: '620px' };
    if (windowId === 'skills') return { w: '900px', h: '700px' };
    if (windowId === 'subscription') return { w: '850px', h: '640px' };
    if (windowId === 'avatar-studio') return { w: '1050px', h: '720px' };
    if (windowId === 'sound') return { w: '900px', h: '700px' };
    if (windowId === 'terminal') return { w: '900px', h: '600px' };
    if (windowId === 'runtime-log') return { w: '980px', h: '680px' };
    if (windowId === 'growth') return { w: '1000px', h: '720px' };
    if (windowId === 'workflow') return { w: '920px', h: '700px' };
    if (windowId === 'file-explorer') return { w: '1000px', h: '720px' };
    if (windowId === 'calendar') return { w: '960px', h: '720px' };
    if (windowId === 'screenshot') return { w: '860px', h: '640px' };
  if (windowId === 'stats') return { w: '900px', h: '680px' };
  if (windowId === 'ecosystem') return { w: '1100px', h: '750px' };
  return { w: '900px', h: '700px' };
  };
  // v3.12.6: useMemo 避免每次渲染全量重建 dockApps（AnimatePresence + layoutId 源）
  const dockApps = useMemo(() => [
    ...appIcons,
    ...openWindows
      .filter(windowId => !appIcons.some(app => app.id === windowId))
      .map(getWindowMeta),
  ], [openWindows, minimizedWindows]);
  const operationModeOptions = [
    {
      id: 'meeting' as const,
      label: t.modeMeeting || (lang === 'zh' ? '会议' : 'Meeting'),
      title: t.modeMeetingTitle || (lang === 'zh' ? '会议模式' : 'Meeting mode'),
      description: t.modeMeetingDesc || (lang === 'zh' ? '自动开启语音转文字，收录会议笔记；结束后整理纪要、分析和报告。' : 'Starts speech-to-text, records meeting notes, then produces a summary, analysis, and report when ended.'),
      hint: t.modeMeetingHint || (lang === 'zh' ? '会议记录' : 'Live notes'),
      icon: <FileText size={16} />,
    },
    {
      id: 'chat' as const,
      label: t.modeChat || (lang === 'zh' ? '聊天' : 'Chat'),
      title: t.modeChatTitle || (lang === 'zh' ? '聊天模式' : 'Chat mode'),
      description: t.modeChatDesc || (lang === 'zh' ? '默认安静交流；明确给出工作指令时，小枫会先说明行动方式再调用能力。' : 'Quiet conversation by default. With a clear work command, 小枫 explains the route before acting.'),
      hint: t.modeChatHint || (lang === 'zh' ? '安静交流' : 'Quiet chat'),
      icon: <MessageSquare size={16} />,
    },
    {
      id: 'assistant' as const,
      label: t.modeAssistant || (lang === 'zh' ? '助手' : 'Assistant'),
      title: t.modeAssistantTitle || (lang === 'zh' ? '助手模式' : 'Assistant mode'),
      description: t.modeAssistantDesc || (lang === 'zh' ? '按任务选择聊天、运行日志、文件工具或桌面操作；开始前先给行动指南。' : 'Chooses chat, run logs, file tools, or desktop control by task, with an action guide first.'),
      hint: t.modeAssistantHint || (lang === 'zh' ? '引导执行' : 'Guided execution'),
      icon: <Sparkles size={16} />,
    },
    {
      id: 'autonomous' as const,
      label: t.modeAutonomy || t.modeAutoExecute || (lang === 'zh' ? '自主' : 'Autonomy'),
      title: t.modeAutonomyTitle || t.modeAutoExecuteTitle || (lang === 'zh' ? '自主模式' : 'Autonomy mode'),
      description: t.modeAutonomyDesc || t.modeAutoExecuteDesc || (lang === 'zh' ? '适合多步任务；小枫会先给行动指南，再用运行日志、桌面控制、命令、工具和团队推进，并展示进度。' : 'For multi-step work. 小枫 gives an action guide, then uses run logs, desktop control, commands, tools, and teams with visible progress.'),
      hint: t.modeAutonomyHint || t.modeAutoExecuteHint || (lang === 'zh' ? '自主推进' : 'Visible autonomous work'),
      icon: <Zap size={16} />,
    },
  ];
  const pendingOperationModeOption = pendingOperationMode
    ? operationModeOptions.find(m => m.id === pendingOperationMode)
    : null;
  const workflowHasExecution = workflowSteps.some(step =>
    step.type === 'background' ||
    step.type === 'confirmation' ||
    step.type === 'tool_start' ||
    step.type === 'tool_result' ||
    step.type === 'error'
  );
  const [forceWorkflowPanel, setForceWorkflowPanel] = useState(false);
  const workflowPanelVisible =
    (!chatOpen && (
      agentStatus !== 'idle' ||
      workflowSteps.length > 0 ||
      workflowHasExecution ||
      backgroundWorkflowTasks.length > 0
    )) || forceWorkflowPanel;

  const tutorialLabel = t.showTutorial || (lang === 'zh' ? '教程' : 'Tutorial');

  return (
    <div
      data-mode={theme === 'editorial' ? 'light' : 'dark'}
      className={`fixed inset-0 overflow-hidden cursor-default select-none transition-colors duration-300 ${
      isWallpaperMode ? 'bg-transparent pointer-events-none' :
      theme === 'celestial' ? 'bg-[#05050a]' :
      theme === 'nebula' ? 'bg-[#050010]' :
      theme === 'cyber' ? 'bg-[#000808]' :
      theme === 'luxury' ? 'bg-[#0a0805]' :
      theme === 'terminal' ? 'bg-[#000500]' :
      theme === 'editorial' ? 'bg-[#eef1f5]' :
      'bg-black'
    }`}
      style={{
        ...(wallpaper === 'custom' && wallpaperUrl ? {
          backgroundImage: `url(${wallpaperUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        } : {}),
      }}
    >
      <input ref={wallpaperInputRef} type="file" accept="image/*" onChange={handleWallpaperUpload} className="hidden" />
      <ContextMenu menu={menu} items={contextItems} onAction={(action) => {
        const result = executeContextMenu(action);
        handleContextAction(result.action, result.context);
      }} />
      <ControlCenter
        isOpen={isControlCenterOpen}
        onClose={() => setIsControlCenterOpen(false)}
        t={t}
        brightness={brightness}
        setBrightness={setBrightness}
        volume={volume}
        setVolume={setVolume}
        lang={lang}
        setLang={setLang}
        toggleWindow={toggleWindow}
      />
      {/* CRT Scanline / Noise Overlay */}
      <div className="fixed inset-0 z-[1000] pointer-events-none opacity-[0.03] bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(255,0,0,0.06),rgba(0,255,0,0.02),rgba(0,0,255,0.06))] bg-[length:100%_2px,3px_100%] select-none" />
      
      {/* Hardcore Boot Screen Overlay */}
      <AnimatePresence>
        {bootVisible && (
          <HardcoreBootSequence onComplete={() => setBootVisible(false)} t={t} />
        )}

        {!bootVisible && hasEverConnected && socketStatus !== 'connected' && (
          <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[500] px-4 py-2 rounded-full bg-black/70 border border-celestial-saturn/30 text-xs font-bold tracking-widest text-celestial-saturn backdrop-blur flex items-center gap-2 shadow-lg">
            <span className="w-2 h-2 rounded-full bg-celestial-saturn animate-pulse" />
            {socketStatus === 'reconnecting' ? (t.reconnecting || '正在重新连接…') : (t.connectionLost || '连接中断，正在重连…')}
          </div>
        )}
      </AnimatePresence>

      {/* Immersive Environment Layer (Wallpaper OS Foundation) */}
      <div 
        className={`fixed inset-0 z-0 overflow-hidden transition-colors duration-300 ${isWallpaperMode ? 'bg-transparent' : 'bg-[#05050a]'}`}
      >
        <div className="absolute inset-0">
          {/* Warp Flash Overlay */}
          <motion.div 
            animate={{ 
              opacity: viewMode === 'world' ? [0, 0.4, 0] : 0,
            }}
            transition={{ duration: 0.8 }}
            className={`absolute inset-0 z-50 pointer-events-none ${
              theme === 'nebula' ? 'bg-[#050010]' : theme === 'cyber' ? 'bg-[#000808]' : 'bg-white'
            }`}
          />

          {/* Global Node Map Background */}
          <div className="absolute inset-0 z-0 pointer-events-none">
             <GlobalNodeMap variant="subtle" />
          </div>

          {/* Personal Desktop Wallpaper Layer */}
          <motion.div
            style={{
              scale: personalScale,
              opacity: personalOpacity,
              z: 500
            }}
            className="absolute inset-0 pointer-events-none"
          >
            <div className="absolute inset-0">
               <AnimatePresence mode="wait">
                {theme === 'celestial' && (
                  <motion.div 
                    key="celestial-wp"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    transition={{ duration: 1 }}
                    className="absolute inset-0"
                  >
                    <div className="star-field opacity-20" />
                    <div className="undulating-bg opacity-30 scale-125" />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/80" />
                  </motion.div>
                )}
                {theme === 'nebula' && (
                  <motion.div 
                    key="nebula-wp"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    transition={{ duration: 1 }}
                    className="absolute inset-0"
                  >
                    <div className="star-field opacity-10" />
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(168,85,247,0.1)_0%,transparent_70%)]" />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/0 to-black/60" />
                  </motion.div>
                )}
                {theme === 'cyber' && (
                  <motion.div 
                    key="cyber-wp"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    transition={{ duration: 1 }}
                    className="absolute inset-0"
                  >
                    <div className="absolute inset-0 bg-[linear-gradient(rgba(16,185,129,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(16,185,129,0.05)_1px,transparent_1px)] bg-[size:40px_40px]" />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/0 to-black/80" />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>

        {/* Hyper-tunnel edges */}
        <div className="absolute inset-0 shadow-[inset_0_0_300px_rgba(0,0,0,1)] pointer-events-none" />
        
        {/* Brightness Overlay */}
        <div 
          className="absolute inset-0 pointer-events-none z-[1000] transition-opacity duration-300" 
          style={{ backgroundColor: 'black', opacity: (100 - brightness) / 100 * 0.7 }} 
        />
      </div>

      {/* Nexus Globe — WebGL 3D Earth with constellation + globe + neural layers */}
      <AnimatePresence>
        {viewMode === 'world' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2 }}
            className="fixed inset-0 z-0"
          >
            <Suspense fallback={<div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-white/20 border-t-celestial-saturn rounded-full animate-spin" /></div>}><NexusGlobeLazy theme={theme as 'celestial' | 'nebula' | 'cyber'} syncRate={syncRate} onAgentSelect={handleAgentNode} pulses={pulses} bursts={bursts.map((b) => ({ agentId: b.agentId, ts: b.ts }))} /></Suspense>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Nexus View HUD (Floating Content that only shows in Nexus mode) */}
      <AnimatePresence>
        {viewMode === 'world' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-20 flex items-center justify-center pointer-events-none"
          >
            <div className="relative z-10 text-center space-y-8 pointer-events-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h2 className="text-6xl font-black text-white/90 tracking-[1.2rem] uppercase drop-shadow-[0_0_30px_rgba(255,255,255,0.1)]">{t.nexusTitle || '中枢'}</h2>
                <div className="mt-4 flex items-center justify-center gap-4">
                  <div className="h-px w-12 bg-gradient-to-r from-transparent to-celestial-saturn/50" />
                  <p className="text-xs text-celestial-saturn font-black tracking-[0.8em] uppercase">{t.distributedOSCore || '分布式操作系统核心'}</p>
                  <div className="h-px w-12 bg-gradient-to-l from-transparent to-celestial-saturn/50" />
                </div>
              </motion.div>

              <motion.button
                onClick={() => setViewMode('personal')}
                className="group px-10 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-xs font-black text-white/60 tracking-[0.4em] uppercase transition-all backdrop-blur-2xl hover:text-white hover:border-white/20"
              >
                {t.focusPersonalTerritory || '专注个人领地'}
              </motion.button>
            </div>

            <div className="absolute left-8 top-24 flex flex-col gap-3 pointer-events-auto">
              <MeshSyncSelector t={t} syncRate={syncRate} onSyncRateChange={setSyncRate} />
              <NexusStatsPanel t={t} orgConnection={orgConnection} />
              <button
                onClick={() => setDrill({ level: 'org' })}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-celestial-saturn/15 hover:bg-celestial-saturn/25 border border-celestial-saturn/25 text-celestial-saturn text-xs font-black tracking-wider transition-colors"
              >
                <LayoutGrid size={14} /> 中枢下钻
              </button>
            </div>
              <NexusDrilldown
                t={t}
                orgConnection={orgConnection}
                workDomain={workDomain}
                pulses={pulses}
                drill={drill}
                setDrill={setDrill}
                onSelectAgent={handleAgentChat}
                onCloseAll={() => setDrill({ level: 'overview' })}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="fixed inset-0 z-[100] pointer-events-none">
        {/* Top Status Bar */}
        <div className={`absolute top-0 inset-x-0 h-10 glass-dark border-b border-white/5 flex items-center px-6 pointer-events-auto backdrop-blur-md transition-all duration-1000 ${isWallpaperMode || musicVisible ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
          <div className="flex items-center gap-6 flex-1">
            <button onClick={() => toggleWindow('home')} className="flex items-center gap-2 group transition-all">
               <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-celestial-mars to-celestial-saturn flex items-center justify-center p-1 group-hover:rotate-12 transition-transform shadow-lg shadow-celestial-saturn/20">
                 <Rocket size={14} className="text-white" />
               </div>
               <span className="text-xs font-black tracking-widest uppercase text-white/60">{t.xiaofengOS || '小枫 OS'}</span>
            </button>
            <div className="h-4 w-px bg-white/10" />
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setQuickReviewMode(true);
                  setShowOnboarding(true);
                }}
                className="flex h-7 items-center gap-1.5 rounded-lg px-2.5 text-[11px] font-black uppercase tracking-widest text-white/50 transition-colors hover:bg-white/10 hover:text-white"
                title={tutorialLabel}
              >
                <Sparkles size={12} />
                {tutorialLabel}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-center">
            <WorkModeSwitch
              domain={workDomain}
              onSelectDomain={switchDomain}
              connected={orgConnection?.connected ?? false}
              organizationOpen={activeTab === 'org'}
              onOpenOrganization={() => setActiveTab('org')}
              onCloseOrganization={() => {
                if (activeTab === 'org') setActiveTab('home');
              }}
            />
          </div>

          <div className="flex items-center gap-6 flex-1 justify-end">
            {/* Dock Bar — v3.12.6: 用 useCallback 稳定回调引用，配合 DesktopDock React.memo */}
            {/* 回调定义在组件体内（JSX 上方），避免每次内联创建新函数 */}
            <DesktopDock
              t={t}
              lang={lang}
              onNewChat={useCallback(() => { setChatOpen(true); setActiveTab('chat'); }, [])}
              onSkills={useCallback(() => toggleWindow('skills'), [toggleWindow])}
              onTheme={useCallback(() => toggleWindow('settings'), [toggleWindow])}
              onSearch={useCallback(() => setIsSearchOpen(true), [])}
              onScreenshot={useCallback(() => toggleWindow('screenshot'), [toggleWindow])}
              callState={callState}
              selectedPet={selectedPet}
              systemStats={systemStats}
            />

            <div className="flex items-center gap-4 text-white/55">
               <button onClick={() => setIsSearchOpen(true)} className="flex items-center gap-1 hover:text-white transition-colors" aria-label={lang === 'zh' ? '搜索' : 'Search'}><Search size={14} /></button>
               <button
                 onClick={() => setIsNotificationPanelOpen(prev => !prev)}
                 className={`flex items-center gap-1 relative transition-colors ${isNotificationPanelOpen ? 'text-white' : 'hover:text-white'}`}
                 aria-expanded={isNotificationPanelOpen}
                 aria-label={t.notificationsLabel || '通知'}
               >
                  <Bell size={14} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 rounded-full bg-red-500 text-xs font-black flex items-center justify-center text-white">
                     {unreadCount > 9 ? '9+' : unreadCount}
                   </span>
                 )}
               </button>
               {/* Server connection status — v3.12.7: a11y */}
               <span
                 className={`w-2 h-2 rounded-full ${socket?.connected ? 'bg-green-400 shadow-[0_0_6px] shadow-green-400/60' : 'bg-red-400 animate-pulse'}`}
                  title={socket?.connected ? (lang === 'zh' ? '服务已连接' : 'Service connected') : (lang === 'zh' ? '服务未连接' : 'Service disconnected')}
                  role="status"
                  aria-label={socket?.connected ? (lang === 'zh' ? '服务已连接' : 'Connected') : (lang === 'zh' ? '服务已断开' : 'Disconnected')}
               />
               {/* Volume mute toggle — v3.12.7: a11y */}
                <button onClick={toggleMute} className="flex items-center gap-1 hover:text-white transition-colors" title={isMuted ? (lang === 'zh' ? '取消静音' : 'Unmute') : (lang === 'zh' ? '静音' : 'Mute')} aria-label={isMuted ? (lang === 'zh' ? '取消静音' : 'Unmute') : (lang === 'zh' ? '静音' : 'Mute')} aria-pressed={isMuted}>
                 {isMuted ? <VolumeX size={14} className="text-red-400" /> : <Volume2 size={14} />}
               </button>
               {/* Battery — real via navigator.getBattery() */}
                <BatteryIndicator lang={lang} />
               <button
                 onClick={toggleWallpaperMode}
                 className={`h-6 px-2 rounded-md border transition-all flex items-center gap-1 text-[12px] font-bold uppercase tracking-wider ${
                   isWallpaperMode
                     ? 'bg-celestial-saturn/20 text-celestial-saturn border-celestial-saturn/30'
                     : 'bg-white/5 border-white/5 text-white/55 hover:bg-white/10 hover:text-white'
                 }`}
                  title={isWallpaperMode ? (lang === 'zh' ? '退出壁纸模式' : 'Exit wallpaper mode') : (lang === 'zh' ? '壁纸模式' : 'Wallpaper mode')}
                  aria-label={isWallpaperMode ? (lang === 'zh' ? '退出壁纸模式' : 'Exit wallpaper') : (lang === 'zh' ? '壁纸模式' : 'Wallpaper')}
                  aria-pressed={isWallpaperMode}
               >
                 <Zap size={10} className={isWallpaperMode ? 'animate-pulse' : ''} />
                 {isWallpaperMode ? 'Fusion' : (lang === 'zh' ? '壁纸' : 'Wallpaper')}
               </button>
            </div>

            {/* v3.12.7: 控制中心入口 + 时间格式尊重 lang */}
            <button
              onClick={() => setIsControlCenterOpen(!isControlCenterOpen)}
              className="flex items-center gap-3 px-3 py-1 bg-white/5 hover:bg-white/10 rounded-full border border-white/5 transition-all group"
              aria-label={lang === 'zh' ? '控制中心' : 'Control Center'}
              aria-expanded={isControlCenterOpen}
              title={lang === 'zh' ? '控制中心' : 'Control Center'}
            >
              <div className="flex flex-col items-end">
                <span className="text-[12px] font-black text-white/80 leading-none">{time.toLocaleTimeString(lang === 'zh' ? 'zh-CN' : 'en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                <span className="text-xs font-bold text-white/55 uppercase tracking-tighter">{time.toLocaleDateString(lang === 'zh' ? 'zh-CN' : 'en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
              </div>
              <Activity size={14} className="text-celestial-saturn group-hover:rotate-180 transition-transform duration-500" />
            </button>

            {/* Window Controls — v3.12.7: a11y */}
            <div className="flex items-center gap-1 ml-2">
              <button
                onClick={handleWindowMinimize}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-white/55 hover:text-white hover:bg-white/10 transition-colors"
                title={lang === 'zh' ? '最小化' : 'Minimize'}
                aria-label={lang === 'zh' ? '最小化' : 'Minimize'}
              >
                <Minus size={14} />
              </button>
              <button
                onClick={handleWindowMaximize}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-white/55 hover:text-white hover:bg-white/10 transition-colors"
                title={lang === 'zh' ? '最大化' : 'Maximize'}
                aria-label={lang === 'zh' ? '最大化' : 'Maximize'}
              >
                <Square size={12} />
              </button>
              <button
                onClick={handleWindowClose}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-white/55 hover:text-white hover:bg-red-500/80 transition-colors"
                title={lang === 'zh' ? '关闭' : 'Close'}
                aria-label={lang === 'zh' ? '关闭' : 'Close'}
              >
                <X size={14} />
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {isNotificationPanelOpen && !isWallpaperMode && !musicVisible && (
            <>
              <motion.button
                type="button"
                aria-label={lang === 'zh' ? '关闭通知' : 'Close notifications'}
                className="fixed inset-x-0 bottom-0 top-10 z-[101] cursor-default pointer-events-auto bg-transparent"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsNotificationPanelOpen(false)}
              />
              <motion.div
                initial={{ opacity: 0, y: -18, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -18, scale: 0.98 }}
                transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
                className="fixed right-6 top-12 z-[102] h-[min(560px,calc(100vh-4.5rem))] w-[min(430px,100vw)] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-white/10 bg-zinc-950/95 shadow-2xl shadow-black/50 pointer-events-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <NotificationCenter
                  onChatMessage={(message) => {
                    setIsNotificationPanelOpen(false);
                    setChatPrefill(message);
                    setChatOpen(true);
                  }}
                />
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Global Control Center handled at top level for proper click detection */}

        {/* Global Search */}
        <AnimatePresence>
          {isSearchOpen && (
            <Spotlight 
              isOpen={isSearchOpen} 
              onClose={() => setIsSearchOpen(false)} 
              onSelect={toggleWindow}
              onOpenConversation={(convId: string) => { setSearchConvId(convId); toggleWindow('chat'); }}
              apps={allAppEntries}
              t={t}
            />
          )}
        </AnimatePresence>

        {/* Bottom Taskbar / Dock — v3.12.4: z-index 注释 + a11y */}
        <div className={`absolute bottom-6 left-1/2 -translate-x-1/2 z-50 h-16 px-4 glass-dark rounded-[2.5rem] border border-white/10 flex items-center gap-2 shadow-2xl backdrop-blur-2xl transition-all duration-1000 will-change-transform ${isWallpaperMode || musicVisible ? 'opacity-0 pointer-events-none' : 'opacity-100 pointer-events-auto'}`}
          role="toolbar"
          aria-label={lang === 'zh' ? '桌面任务栏' : 'Desktop taskbar'}>
          <button 
            onClick={() => setViewMode(viewMode === 'personal' ? 'world' : 'personal')}
            aria-label={viewMode === 'world' ? (t.personalView || '个人视角') : (t.nexusView || '中枢视角')}
            className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all group relative ${
              viewMode === 'world' ? 'bg-celestial-saturn text-black' : 'bg-white/5 text-white/40 hover:bg-white/10'
            }`}
          >
            {viewMode === 'world' ? <Globe size={24} /> : <Monitor size={24} />}
            <div className="absolute -top-12 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/80 rounded-lg text-xs font-black uppercase text-white opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              {viewMode === 'world' ? (t.personalView || '个人视角') : (t.nexusView || '中枢视角')}
            </div>
          </button>
          <button
            onClick={() => setKnowledgeOpen(prev => !prev)}
            aria-label={t.knowledgeBase || '知识库'}
            className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all group relative ${
              knowledgeOpen
                ? 'bg-gradient-to-br from-celestial-glow to-celestial-saturn text-black shadow-lg'
                : 'bg-white/5 text-white/40 hover:bg-white/10 hover:text-white'
            }`}
          >
            <BrainCircuit size={24} />
            <div className="absolute -top-12 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/80 rounded-lg text-xs font-black uppercase text-white opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              {t.knowledgeBase || '知识库'}
            </div>
            </button>
          <button
            onClick={openThemeCenter}
            className="w-12 h-12 rounded-2xl flex items-center justify-center transition-all group relative bg-white/5 text-white/40 hover:bg-white/10 hover:text-celestial-saturn"
            title={t.themeCenter || '主题中心'}
            aria-label={t.themeCenter || '主题中心'}
          >
            <Palette size={22} />
            <div className="absolute -top-12 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/80 rounded-lg text-xs font-black uppercase text-white opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              {t.themeCenter || '主题中心'}
            </div>
          </button>
          <div className="h-8 w-px bg-white/10 mx-2" />
          <AnimatePresence>
            {dockApps.map(app => {
              const isActive = openWindows.includes(app.id) || (app.id === 'chat' && chatOpen);
              return (
              <motion.button
                key={app.id}
                layoutId={`dock-${app.id}`}
                onClick={() => toggleWindow(app.id)}
                aria-label={app.label}
                className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all group relative ${
                  isActive
                    ? `bg-gradient-to-br ${app.id === focusedWindow || app.id === 'chat' ? app.color : 'from-white/10 to-white/5'} text-white shadow-lg ${minimizedWindows.includes(app.id) ? 'opacity-40 translate-y-2' : ''}`
                    : 'bg-white/5 text-white/40 hover:bg-white/10'
                }`}
              >
                {app.icon}
                {isActive && (
                  <motion.div
                    layoutId={`indicator-${app.id}`}
                    className={`absolute -bottom-1 left-1/2 -translate-x-1/2 rounded-full ${minimizedWindows.includes(app.id) ? 'w-3 h-0.5 bg-white/40' : 'w-1 h-1 bg-white'}`}
                  />
                )}
                {/* Taskbar Preview Tooltip */}
                {isActive && !minimizedWindows.includes(app.id) && (
                   <div className="absolute -top-28 left-1/2 -translate-x-1/2 w-36 bg-black/90 border border-white/10 rounded-xl overflow-hidden opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none shadow-2xl">
                      <div className="p-3 flex items-center gap-2 border-b border-white/5">
                        <div className="w-6 h-6 rounded-lg bg-white/10 flex items-center justify-center">
                          <span className="scale-75">{app.icon}</span>
                        </div>
                        <span className="text-xs font-bold text-white/80 truncate">{app.label}</span>
                      </div>
                      <div className="px-3 py-2">
                        <p className="text-[12px] text-white/55 leading-tight">
                          {focusedWindow === app.id ? (t.activeFocused || '活跃 — 已聚焦') : (t.openInBackground || '后台打开')}
                        </p>
                      </div>
                   </div>
                )}
                <div className="absolute -top-12 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/80 rounded-lg text-xs font-black uppercase text-white opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  {app.label}
                </div>
              </motion.button>
              );
            })}
          </AnimatePresence>
          <div className="h-8 w-px bg-white/10 mx-2" />
          {user ? (
            <button
              onClick={() => toggleWindow('profile')}
              className="w-12 h-12 rounded-2xl overflow-hidden border-2 border-white/10 hover:border-celestial-saturn/50 bg-white/5 flex items-center justify-center transition-all group"
              aria-label={lang === 'zh' ? '个人资料' : 'Profile'}
            >
              {user.photoURL ? (
                <img src={user.photoURL} alt={user.displayName || ''} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
              ) : (
                <UserIcon size={20} className="text-white/40 group-hover:text-white/80 transition-colors" />
              )}
            </button>
          ) : (
            <button
              onClick={onLogin}
              className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 text-white/40 hover:text-white hover:bg-white/10 hover:border-celestial-saturn/30 transition-all flex items-center justify-center group"
              aria-label={lang === 'zh' ? '登录' : 'Login'}
            >
              <UserIcon size={20} className="group-hover:text-celestial-saturn transition-colors" />
            </button>
          )}
        </div>
      </div>

      {/* Main OS Content Layer (Personal Desktop Surface) */}
      <motion.div
        style={{
          scale: personalScale,
          opacity: personalOpacity,
        }}
        className={`absolute inset-0 z-[15] flex flex-col ${viewMode === 'world' ? 'pointer-events-none' : ''}`}
      >
        <div className="relative w-full h-full pointer-events-auto">
          {/* Central Interactive Entity — hidden when music layer is active */}
          <div className={`absolute inset-0 flex items-center justify-center z-[15] pointer-events-none ${musicVisible ? 'opacity-0 pointer-events-none' : ''}`}>
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2, ease: "easeOut" }}
          className="relative pointer-events-auto scale-75 opacity-90 transition-all"
        >
          <div className="relative flex flex-col items-center">
            {selectedPet ? (
              <div className="relative group flex flex-col items-center gap-3">
                <button
                  onClick={() => toggleWindow('avatar-studio')}
                  className={`cursor-pointer transition-all ${callState !== 'idle' ? 'animate-pulse' : ''}`}
                  title={lang === 'zh' ? `${selectedPet.name} - 点击打开形象设计室` : `${selectedPet.name} - open 形象 Studio`}
                >
                  <PetAvatar
                    pet={selectedPet}
                    animation={
                      petReaction ? petReaction.animation as any :
                      callState === 'speaking' ? 'wave' :
                      callState === 'listening' ? 'idle' :
                      callState !== 'idle' ? 'jump' : 'idle'
                    }
                    accessoryIds={equippedAccessories}
                    scale={1.2}
                    audioLevel={audioLevel}
                    callState={callState}
                    behavior={
                      'playful'
                    }
                  />
                </button>
                {/* Voice call button below pet */}
                <button
                  onClick={callState === 'idle' ? startStandardVoiceCall : endVoiceCallFromUI}
                  className={`w-12 h-12 rounded-full border transition-all flex items-center justify-center ${
                    callState !== 'idle'
                      ? 'bg-red-500/20 border-red-500/40 text-red-400'
                      : 'bg-white/5 border-white/10 text-white/40 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {callState !== 'idle' ? <Mic size={20} className="animate-pulse" /> : <Mic size={20} />}
                </button>
                <MeetingModeButton
                  t={t}
                  lang={lang}
                  active={operationMode === 'meeting'}
                  live={operationMode === 'meeting' && callState !== 'idle'}
                  onClick={openMeetingMode}
                />
                {/* Reset to sphere button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPet(null);
                    savePetPrefsToServer(null, equippedAccessories);
                    toast.info(lang === 'zh' ? '已切换回粒子人脸' : 'Switched back to particle face');
                  }}
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white/10 border border-white/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500/30 hover:border-red-500/40"
                  title={lang === 'zh' ? '切换回粒子人脸' : 'Switch back to particle face'}
                >
                  <X size={10} className="text-white/60" />
                </button>
              </div>
            ) : (
              <>
              {/* Biometrics presence indicator — above particle sphere */}
            <div className="absolute -top-10 left-1/2 -translate-x-1/2 z-30">
              <PresenceIndicator
                status={presence.status}
                faceConfidence={faceRecognition.result.confidence}
                voiceConfidence={voiceprint.result.confidence}
              />
            </div>
            <LocalAgentSphere
                t={t}
                sentiment={sphereSentiment}
                callState={callState}
                audioLevel={audioLevel}
                highPerformance={isTauri}
                isWallpaperMode={isWallpaperMode}
                reaction={petReaction?.animation || null}
                onStartCall={startStandardVoiceCall}
                onEndCall={endVoiceCallFromUI}
                onInterrupt={interrupt}
                onToggleMute={toggleMute}
                onMessage={() => {}}
                facePresent={faceRecognition.result.facePresent}
                gesturesDisabled={false}
                isLightMode={false}
              />
              <div className="mt-2">
                <MeetingModeButton
                  t={t}
                  lang={lang}
                  active={operationMode === 'meeting'}
                  live={operationMode === 'meeting' && callState !== 'idle'}
                  onClick={openMeetingMode}
                />
              </div>
              {wake启用d && wakeWord.isListening && callState === 'idle' && (
                <div className="mt-2 text-xs text-white/45 uppercase tracking-[0.25em] font-mono">
                  {lang === 'zh' ? '正在监听 "小枫"' : 'Listening for "小枫"'}
                </div>
              )}
              {wake启用d && wakeWord.error && (
                <div className="mt-2 text-xs text-amber-400/70 font-mono max-w-[220px] text-center leading-relaxed">
                  {wakeWord.error}
                </div>
              )}
              {wake启用d && !wakeWord.isListening && !wakeWord.error && callState === 'idle' && (
                <div className="mt-2 text-xs text-yellow-400/40 font-mono">
                  {lang === 'zh' ? '唤醒词初始化中...' : 'Wake word initializing...'}
                </div>
              )}
              {!wake启用d && callState === 'idle' && (
                <div className="mt-2 text-xs text-white/30 font-mono">
                  {lang === 'zh' ? '唤醒词未开启' : 'Wake word off'}
                </div>
              )}
              </>
            )}

            <div className={`flex flex-col items-center gap-4 mt-8 transition-all duration-1000 ${isWallpaperMode ? 'opacity-0 blur-sm pointer-events-none' : 'opacity-100'}`}>
              <VoicePicker t={t} />

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full"
              >
                 <div className="flex flex-col items-center gap-1 group">
                   <span className="text-xs font-black tracking-[0.4em] text-white/40 uppercase group-hover:text-celestial-saturn transition-colors">
                     {callState === 'idle' ? (t.lumiNeuralCore || '小枫神经核心') : `${callState.toUpperCase()} ${t.sessionActive || '会话'}`}
                   </span>
                   <div className="flex gap-1">
                     {callState !== 'idle' ? (
                       [1,2,3,4,5].map(i => (
                         <motion.div 
                           key={i} 
                           className="w-1 bg-celestial-saturn rounded-full" 
                           animate={{ height: [8, 16 + audioLevel * 20, 8] }}
                           transition={{ duration: 0.5, repeat: Infinity, delay: i * 0.1 }}
                         />
                       ))
                     ) : (
                       [1,2,3].map(i => <div key={i} className="w-1 h-1 rounded-full bg-celestial-saturn/40 animate-pulse" style={{ animationDelay: `${i*0.2}s` }} />)
                     )}
                   </div>

                   <AnimatePresence>
                     {callState !== 'idle' && transcript && (
                       <motion.div
                         initial={{ opacity: 0, y: 20 }}
                         animate={{ opacity: 1, y: 0 }}
                         exit={{ opacity: 0, scale: 0.9 }}
                         className="mt-6 w-fit max-w-[min(92vw,32rem)] px-6 py-4 bg-white/5 backdrop-blur-3xl border border-white/10 rounded-2xl text-center shadow-2xl"
                       >
                         <p className="max-w-full whitespace-normal break-words text-white/80 text-sm font-medium leading-relaxed italic">
                           "{transcript}"
                         </p>
                         <div className="mt-2 flex justify-center gap-1">
                            <div className="w-1 h-1 rounded-full bg-celestial-saturn animate-pulse" />
                            <div className="w-1 h-1 rounded-full bg-celestial-saturn animate-pulse delay-75" />
                            <div className="w-1 h-1 rounded-full bg-celestial-saturn animate-pulse delay-150" />
                         </div>
                       </motion.div>
                     )}
                   </AnimatePresence>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Desktop Grid & Widgets */}
      <div className={`relative z-10 w-full h-full p-8 md:p-12 lg:p-16 overflow-y-auto custom-scrollbar pt-20 transition-all duration-1000 ${isWallpaperMode ? 'opacity-0 blur-sm pointer-events-none' : 'opacity-100'}`}>
        <div className="flex flex-col xl:flex-row justify-between items-start gap-12">
            <div className="relative flex-1 w-full min-h-[400px]" style={{ margin: 0, padding: 0 }}>
              {desktopIcons.map((def, i) => {
                const iconPos = resolvedIconPositions[def.windowId];
                const x = iconPos.x;
                const y = iconPos.y;
                const label = (t as any)[def.labelKey] || def.labelKey;
                const isIconOpen = openWindows.includes(def.windowId) || (def.windowId === 'workflow' && forceWorkflowPanel);
                const isIconFocused = focusedWindow === def.windowId;
                const handleClick = () => {
                  if (def.windowId === 'workflow') {
                    setForceWorkflowPanel(v => !v);
                  } else {
                    toggleWindow(def.windowId);
                  }
                };
                return (
                  <motion.div
                    key={def.id}
                    drag
                    dragMomentum={false}
                    dragElastic={0}
                    onDragStart={() => { iconDraggedRef.current = true; }}
                    onDragEnd={(_e, info) => {
                      // 拖拽结束：基于当前位置 + 位移量保存新坐标（限制不出左上边界）
                      const nx = Math.max(0, Math.round((x + info.offset.x - ICON_ORIGIN_X) / ICON_STEP_X) * ICON_STEP_X + ICON_ORIGIN_X);
                      const ny = Math.max(0, Math.round((y + info.offset.y) / ICON_STEP_Y) * ICON_STEP_Y);
                      saveIconPosition(def.windowId, nx, ny);
                      // 稍后复位标志，让紧随其后的 click 被忽略
                      setTimeout(() => { iconDraggedRef.current = false; }, 0);
                    }}
                    whileDrag={{ scale: 1.1, zIndex: 50, cursor: 'grabbing' }}
                    onDoubleClick={handleClick}
                    onClick={() => {
                      if (iconDraggedRef.current) return; // 拖拽后不打开窗口
                      handleClick();
                    }}
                    onContextMenu={(e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      showContextMenu(e.clientX, e.clientY, { type: 'icon', targetId: def.windowId });
                    }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1, left: x, top: y }}
                    style={{ position: 'absolute' }}
                    className={`desktop-icon group z-10 select-none cursor-grab active:cursor-grabbing ${isIconOpen ? 'desktop-icon-open' : ''} ${isIconFocused ? 'desktop-icon-focused' : ''}`}
                    role="button"
                    tabIndex={0}
                    aria-label={label}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); }
                    }}
                  >
                    <div className={`desktop-icon-img bg-gradient-to-br ${def.colorClass} shadow-lg shadow-black/40`}>
                      <div className="text-white transition-all duration-200 group-hover:-translate-y-0.5 group-hover:scale-105 group-hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.25)]">
                        {def.icon}
                      </div>
                    </div>
                    <span className="desktop-icon-label truncate">{label}</span>
                  </motion.div>
                );
              })}
            </div>

            <div className="flex flex-col gap-6 w-full lg:w-[440px]">
              {/* Date & Weather Widget — 单独受 xf_calendar_enabled 控制 */}
              {calendar启用d && !isWallpaperMode && (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.15 }}
                >
                  <DateWeatherWidget t={t} lang={lang} />
                </motion.div>
              )}

              {/* Modern Widgets Grid */}
              <ThemeWidget
                t={t}
                lang={lang}
                theme={theme}
                setTheme={setTheme}
                operationMode={operationMode}
                onModeChange={requestOperationModeChange}
              />

              {/* Daily Plans Widget (学习流) — 单独受 xf_learning_flow_enabled 控制 */}
              {learningFlow启用d && !isWallpaperMode && (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <DailyPlans t={t} onOpenQueue={() => toggleWindow('plans')} />
                </motion.div>
              )}

              {/* Notification Preview */}
              {notifications.filter(n => !n.read).length > 0 && (
                <GlassCard className="p-5 rounded-[2rem] space-y-2 border-white/5 bg-black/30 backdrop-blur-3xl cursor-pointer hover:bg-white/[0.06] transition-all" onClick={() => toggleWindow('notifications')}>
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-black uppercase tracking-widest text-white/55 flex items-center gap-2">
                      <Bell size={12} className="text-amber-400" /> {t.recent || '最近'} ({unreadCount} {t.unread || '未读'})
                    </h4>
                    <ChevronRight size={12} className="text-white/45" />
                  </div>
                  <div className="space-y-1">
                    {notifications.filter(n => !n.read).slice(0, 3).map(n => (
                      <div key={n.id} className="text-[12px] text-white/50 truncate">
                        <span className="text-white/70 font-bold">{n.title}</span> — {n.message}
                      </div>
                    ))}
                  </div>
                </GlassCard>
              )}

              {/* Neural Synthesis Monitor — below widgets column */}
              {neuralSynthesis启用d && !isWallpaperMode && (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <NeuralSynthesisMonitor t={t} />
                </motion.div>
              )}

              {/* Mijia IoT Devices — 独立于神经合成监控，单独受 xf_mijia_enabled 控制 */}
              {mijia启用d && !isWallpaperMode && (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.25 }}
                >
                  <MijiaPanel t={t} />
                </motion.div>
              )}

              {wearable启用d && !isWallpaperMode && (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.25 }}
                >
                  <WearablePanel t={t} />
                </motion.div>
              )}

              {carControl启用d && !isWallpaperMode && (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <CarPanel t={t} />
                </motion.div>
              )}

              {/* Runtime Log — 独立于上方，单独受 xf_runtime_log_enabled 控制 */}
              {runtimeLog启用d && !isWallpaperMode && (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="h-[520px]"
                >
                  <RuntimeLogPanel t={t} />
                </motion.div>
              )}
            </div>
        </div>
      </div>

      {/* MCP Live Activity — xiaozhi ⇄ 小枫 */}
      <AnimatePresence>
        {showMcpPanel && mcpActivities.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-28 right-6 z-[60] w-72 pointer-events-auto"
          >
            <GlassCard className="p-4 rounded-2xl border-white/10 bg-black/70 backdrop-blur-2xl space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-[12px] font-black text-white/40 uppercase tracking-widest">{t.liveDeviceLabel || '实时'} · xiaozhi ⇄ 小枫</span>
              </div>
              <div className="space-y-1 max-h-48 overflow-y-auto custom-scrollbar">
                {mcpActivities.slice(0, 5).map((act) => (
                  <div key={act.id} className="text-[12px] text-white/60 border-l-2 border-white/10 pl-2">
                    <span className="text-white/80 font-bold">{act.action === 'create_ppt' ? 'PPT' : act.action === 'chat' ? 'Chat' : act.action}</span>
                    {' · '}
                    <span className={act.status === 'completed' ? 'text-green-400' : act.status === 'failed' ? 'text-red-400' : 'text-celestial-saturn'}>
                      {act.status}
                    </span>
                    {act.message && <div className="text-white/55 truncate">{act.message.slice(0, 60)}</div>}
                    {act.title && <div className="text-white/50">{act.title} ({act.slidesCount} slides)</div>}
                    {act.path && <div className="text-green-400/60 truncate">Saved: {act.path.split('\\').pop()}</div>}
                    {act.toolCalls !== undefined && act.toolCalls > 0 && <div className="text-celestial-saturn/60">Used {act.toolCalls} tool(s)</div>}
                    {act.error && <div className="text-red-400/60 truncate">{act.error.slice(0, 80)}</div>}
                  </div>
                ))}
              </div>
            </GlassCard>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {meetingNotesOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[62] pointer-events-auto bg-black/[0.72] p-3 backdrop-blur-2xl md:p-6"
          >
            <GlassCard className="flex h-full w-full flex-col overflow-hidden rounded-[2rem] border-cyan-400/20 bg-black/[0.86] p-4 shadow-2xl backdrop-blur-2xl md:p-6">
              <div className="flex shrink-0 items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${operationMode === 'meeting' && callState !== 'idle' ? 'bg-cyan-400 animate-pulse' : 'bg-white/25'}`} />
                    <h3 className="text-sm font-black uppercase tracking-[0.18em] text-white/80">
                      {t.meetingMode || (lang === 'zh' ? '会议模式' : 'Meeting Mode')}
                    </h3>
                  </div>
                  <p className="mt-1 text-[11px] leading-relaxed text-white/45">
                    {operationMode === 'meeting'
                      ? (lang === 'zh' ? '正在自动语音转文字并收录笔记' : 'Recording speech-to-text notes automatically')
                      : (lang === 'zh' ? '会议笔记已暂停' : 'Meeting notes paused')}
                  </p>
                  {legalMeetingCaseTitle && (
                    <p className="mt-1 text-[11px] leading-relaxed text-cyan-200/70">
                      {lang === 'zh' ? `归档到案件：${legalMeetingCaseTitle}` : `Archiving to case: ${legalMeetingCaseTitle}`}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={copyMeetingNotes}
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-white/45 transition-colors hover:bg-white/10 hover:text-white"
                    title={lang === 'zh' ? '复制笔记' : 'Copy notes'}
                  >
                    <Copy size={14} />
                  </button>
                  <button
                    onClick={downloadMeetingNotes}
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-white/45 transition-colors hover:bg-white/10 hover:text-white"
                    title={lang === 'zh' ? '导出 Markdown' : 'Export Markdown'}
                  >
                    <Download size={14} />
                  </button>
                  <button
                    onClick={() => setMeetingNotesOpen(false)}
                    className="flex h-9 items-center justify-center gap-2 rounded-xl border border-cyan-400/20 bg-cyan-400/10 px-3 text-xs font-black uppercase tracking-[0.14em] text-cyan-100 transition-colors hover:bg-cyan-400/15"
                    aria-label={lang === 'zh' ? '退出全屏，继续录制' : 'Exit fullscreen, keep recording'}
                    title={lang === 'zh' ? '退出全屏，继续录制' : 'Exit fullscreen, keep recording'}
                  >
                    <X size={15} />
                    <span>{lang === 'zh' ? '退出全屏' : 'Exit'}</span>
                  </button>
                </div>
              </div>

              <div className="mt-5 grid shrink-0 grid-cols-3 gap-2 text-center md:gap-3">
                <div className="rounded-lg border border-white/10 bg-white/[0.03] px-2 py-2">
                  <div className="text-[10px] font-black uppercase tracking-widest text-white/30">{lang === 'zh' ? '状态' : 'State'}</div>
                  <div className="mt-1 text-xs font-bold text-cyan-300">{callState === 'idle' ? 'Idle' : callState}</div>
                </div>
                <div className="rounded-lg border border-white/10 bg-white/[0.03] px-2 py-2">
                  <div className="text-[10px] font-black uppercase tracking-widest text-white/30">{lang === 'zh' ? '条目' : 'Items'}</div>
                  <div className="mt-1 text-xs font-bold text-white/75">{meetingNotes.length}</div>
                </div>
                <div className="rounded-lg border border-white/10 bg-white/[0.03] px-2 py-2">
                  <div className="text-[10px] font-black uppercase tracking-widest text-white/30">{lang === 'zh' ? '时长' : 'Time'}</div>
                  <div className="mt-1 text-xs font-bold text-white/75">
                    {meetingStartedAt ? `${Math.max(0, Math.floor((time.getTime() - meetingStartedAt) / 60000))}m` : '0m'}
                  </div>
                </div>
              </div>

              <div className="mt-5 min-h-0 flex-1 space-y-3 overflow-y-auto pr-1 custom-scrollbar">
                {meetingReportGenerating && (
                  <div className="rounded-xl border border-cyan-400/20 bg-cyan-400/10 px-4 py-3 text-xs font-bold text-cyan-200">
                    {lang === 'zh' ? '小枫 正在整理会议报告...' : '小枫 is preparing the meeting report...'}
                  </div>
                )}
                {meetingReport && !meetingReportGenerating && (
                  <div className="rounded-xl border border-cyan-400/20 bg-cyan-400/10 px-4 py-3">
                    <div className="text-[10px] font-black uppercase tracking-widest text-cyan-300/80">
                      {lang === 'zh' ? '小枫 会议报告' : '小枫 Report'}
                    </div>
                    <pre className="mt-2 whitespace-pre-wrap text-xs leading-relaxed text-white/75 font-sans">{meetingReport}</pre>
                  </div>
                )}
                {meetingNotes.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-white/10 px-4 py-8 text-center text-xs leading-relaxed text-white/35">
                    <Loader2 size={24} className="animate-spin mx-auto mb-2 text-white/15" />
                    {lang === 'zh' ? '进入会议模式后，说话内容会自动出现在这里。' : 'Speech captured in meeting mode will appear here automatically.'}
                  </div>
                ) : (
                  meetingNotes.slice(-80).reverse().map(note => (
                    <div key={note.id} className="border-l border-cyan-400/25 pl-3">
                      <div className="text-[10px] font-black uppercase tracking-widest text-cyan-300/70">{formatMeetingTime(note.time)}</div>
                      <p className="mt-1 text-sm leading-relaxed text-white/70">{note.text}</p>
                    </div>
                  ))
                )}
              </div>

              <div className="mt-5 flex shrink-0 flex-col gap-2 border-t border-white/10 pt-4 sm:flex-row">
                <button
                  onClick={() => void endMeetingAndReport()}
                  disabled={meetingReportGenerating}
                  className="flex-1 rounded-lg border border-cyan-400/20 bg-cyan-400/10 px-3 py-2 text-xs font-black uppercase tracking-widest text-cyan-200 transition-colors hover:bg-cyan-400/15"
                >
                  {meetingReportGenerating
                    ? (lang === 'zh' ? '整理中' : 'Preparing')
                    : (lang === 'zh' ? '结束会议并整理' : 'End & Report')}
                </button>
                <button
                  onClick={clearMeetingNotes}
                  className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-black uppercase tracking-widest text-white/40 transition-colors hover:bg-white/10 hover:text-white"
                >
                  {lang === 'zh' ? '清空' : 'Clear'}
                </button>
              </div>
            </GlassCard>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Workflow Status Panel — breathing lights + step log */}
      <AnimatePresence>
        {pendingOperationModeOption && !chatOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[99990] flex items-center justify-center bg-black/55 px-4 backdrop-blur-sm"
            onClick={() => setPendingOperationMode(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.96 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-2xl border border-cyan-400/20 bg-zinc-950/95 p-5 shadow-2xl"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cyan-400/10 text-cyan-300">
                  {pendingOperationModeOption.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-black uppercase tracking-[0.18em] text-cyan-300">
                    {t.confirmModeSwitch || '确认切换模式'}
                  </div>
                  <h3 className="mt-1 text-lg font-black text-white">{pendingOperationModeOption.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/60">{pendingOperationModeOption.description}</p>
                  <div className="mt-3 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-xs leading-relaxed text-white/45">
                    {pendingOperationMode === 'meeting'
                      ? (t.modeMeetingConfirmNote || '会议模式会开启麦克风语音转文字、记录要点，并在结束时生成报告。')
                      : (t.modeAutoConfirmNote || (lang === 'zh' ? '自主模式可以使用工具、运行日志、团队、命令和桌面控制；进度会可见，敏感操作仍会确认。' : 'Autonomy can use tools, run logs, teams, commands, and desktop control with visible progress and confirmations for sensitive actions.'))}
                  </div>
                </div>
              </div>
              <div className="mt-5 flex items-center justify-end gap-2">
                <button
                  onClick={() => setPendingOperationMode(null)}
                  className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-xs font-black uppercase tracking-widest text-white/50 transition-colors hover:bg-white/10 hover:text-white"
                >
                  {t.cancel || '取消'}
                </button>
                <button
                  onClick={confirmOperationModeChange}
                  className="rounded-lg border border-cyan-400/25 bg-cyan-400/15 px-4 py-2 text-xs font-black uppercase tracking-widest text-cyan-200 transition-colors hover:bg-cyan-400/25"
                >
                  {t.enterMode || '进入模式'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <WorkflowPanel
        visible={workflowPanelVisible}
        agentStatus={agentStatus}
        steps={workflowSteps}
        t={t}
        placement={isWallpaperMode ? 'center' : 'corner'}
        backgroundTasks={backgroundWorkflowTasks}
        onCancelBackgroundTask={cancelBackgroundWorkflowTask}
      />
      <AnimatePresence>
        {wallpaperWorkPromptVisible && !isWallpaperMode && !chatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 18, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.96 }}
            transition={{ duration: 0.24 }}
            className="fixed bottom-28 left-1/2 z-[265] w-[min(520px,calc(100vw-2rem))] -translate-x-1/2 pointer-events-auto"
          >
            <div className="flex items-center gap-3 rounded-2xl border border-cyan-400/20 bg-zinc-950/92 px-3 py-3 shadow-2xl shadow-cyan-950/30 backdrop-blur-2xl sm:px-4">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-cyan-300/20 bg-cyan-300/10 text-cyan-200">
                <Zap size={17} className="animate-pulse" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[11px] font-black uppercase tracking-[0.18em] text-cyan-200">
                  {t.wallpaperWorkPromptTitle || (lang === 'zh' ? '进入原生桌面协作' : 'Native desktop handoff')}
                </div>
                <div className="mt-0.5 truncate text-xs font-medium text-white/55">
                  {t.wallpaperWorkPromptDesc || (lang === 'zh' ? '小枫已开始工作，可以直接转入壁纸模式。' : '小枫 has started working and can move into wallpaper mode.')}
                </div>
              </div>
              <button
                onClick={enterWallpaperFromWorkPrompt}
                className="flex shrink-0 items-center gap-1.5 rounded-lg border border-cyan-300/25 bg-cyan-300/15 px-3 py-2 text-xs font-black uppercase tracking-widest text-cyan-100 transition-colors hover:bg-cyan-300/25"
              >
                <Zap size={13} />
                {t.enterWallpaper || (lang === 'zh' ? '进入壁纸' : 'Enter')}
              </button>
              <button
                onClick={dismissWallpaperWorkPrompt}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] text-white/45 transition-colors hover:bg-white/10 hover:text-white"
                aria-label={t.close || '关闭'}
              >
                <X size={14} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="absolute inset-0 z-[30] pointer-events-none">
        <DesktopOnboarding 
          isOpen={showOnboarding}
          sensorPrimerSeen={sensorPrimerSeen}
          onSensorPrimerDone={finishSensorPrimer}
          key={`${quickReviewMode}-${onboardingStartStep}`}
          quickReview={quickReviewMode}
          startStep={onboardingStartStep}
          onQuickReviewSelect={(index) => {
            setOnboardingStartStep(index);
            setQuickReviewMode(false);
          }}
          onFinish={() => {
            setShowOnboarding(false);
            setQuickReviewMode(false);
            setOnboardingStartStep(0);
            localStorage.setItem('xf_onboarding_seen', 'true');
            if (!sensorPrimerSeen) {
              localStorage.setItem('xf_sensor_primer_seen', 'true');
              setSensorPrimerSeen(true);
            }
            // 首次完成教程后显示引导提示
            if (!quickReviewMode) setShowTooltips(true);
          }}
          t={t}
        />
        <ContextualTooltips isZh={t?.langCode !== 'en'} enabled={showTooltips} />
        <VoiceTrainingDialog 
          isOpen={isTrainingOpen} 
          onClose={() => setIsTrainingOpen(false)} 
          onSuccess={() => window.dispatchEvent(new CustomEvent('xf:voice-updated'))}
        />
        <AnimatePresence>
          {openWindows.map(windowId => {
            const size = getWindowSize(windowId);
            const orderIdx = windowOrder.indexOf(windowId);
            const meta = getWindowMeta(windowId);
            return (
              <OSWindow
                key={windowId}
                id={windowId}
                title={meta.label}
                icon={meta.icon}
                isActive={focusedWindow === windowId}
                isMinimized={minimizedWindows.includes(windowId)}
                zIndex={10 + (orderIdx >= 0 ? orderIdx : 0)}
                onFocus={(id) => {
                  setFocusedWindow(id);
                  setWindowOrder(prev => [...prev.filter(w => w !== id), id]);
                }}
                onMinimize={(id) => setMinimizedWindows(prev => [...prev, id])}
                onMinimizeComplete={(id) => {
                  // Window stays in DOM, just mark animation complete
                }}
                onClose={() => closeWindow(windowId)}
                colorClass={meta.color}
                width={size.w}
                height={size.h}
                t={t}
              >
                <div className="os-window-body custom-scrollbar">
                  {windowId === 'kernel' ? (
                    <KernelMonitorApp t={t} />
                  ) : windowId === 'settings' ? (
                    <Settings t={t} lang={lang} setLang={setLang} theme={theme} setTheme={setTheme} activeSection={settingsSection} onSectionChange={setSettingsSection} />
                  ) : windowId === 'music' ? (
                    <div className="flex flex-col items-center justify-center h-full text-center space-y-8 animate-in zoom-in-95 duration-500">
                      <div className="relative">
                        <Disc size={120} className="text-celestial-saturn animate-[spin_8s_linear_infinite]" />
                        <Headphones size={40} className="absolute -bottom-4 -right-4 text-white p-2 bg-black rounded-full" />
                      </div>
                      <div className="space-y-2">
                        <h2 className="text-3xl font-black uppercase tracking-tighter text-white">{t.mediaCenter || '媒体中心'}</h2>
                        <p className="text-white/40 max-w-md text-sm">{t.mediaCenterDesc || '语音合成、媒体播放和音频设置。'}</p>
                      </div>
                      <div className="flex gap-4">
                        <button onClick={() => { toggleWindow('settings'); setSettingsSection('voice'); }} className="px-6 py-3 bg-celestial-saturn/10 border border-celestial-saturn/30 rounded-2xl text-xs font-black uppercase tracking-widest text-celestial-saturn hover:bg-celestial-saturn/20 transition-all">
                          {t.voiceForge || '语音锻造'}
                        </button>
                        <button onClick={() => { toggleWindow('settings'); setSettingsSection('voice-services'); }} className="px-6 py-3 bg-white/5 border border-white/10 rounded-2xl text-xs font-black uppercase tracking-widest text-white/40 hover:bg-white/10 transition-all">
                          {t.mediaServices || '媒体服务'}
                        </button>
                      </div>
                    </div>
                  ) : windowId === 'music-center' ? (
                    <MusicCenter isOpen={true} onClose={() => closeWindow('music-center')} t={t} />
                  ) : windowId === 'personality' ? (
                    <ErrorBoundary componentName="人格编辑器"><PersonalityEditor t={t} /></ErrorBoundary>
                  ) : windowId === 'tools' ? (
                    <ToolPanel />
                  ) : windowId === 'team' ? (
                    <TeamHub t={t} />
                  ) : windowId === 'github-mcp' ? (
                    <GitHubMCPBrowser t={t} />
                  ) : windowId === 'notifications' ? (
                    <NotificationCenter
                      onChatMessage={(message) => {
                        closeWindow('notifications');
                        setChatPrefill(message);
                        setChatOpen(true);
                      }}
                    />
                  ) : windowId === 'reminders' ? (
                    <ReminderPanel t={t} />
                  ) : windowId === 'plans' ? (
                    <ExecutionWorkQueue t={t} />
                  ) : windowId === 'runtime-log' ? (
                    <RuntimeLogPanel t={t} />
                  ) : windowId === 'devices' ? (
                    <DeviceSyncCenter t={t} />
                  ) : windowId === 'tokens' ? (
                    <TokenDashboard />
                  ) : windowId === 'skills' ? (
                    <SkillCenter t={t} lang={lang} />
                  ) : windowId === 'subscription' ? (
                    <SubscriptionPanel t={t} />
                  ) : windowId === 'avatar-studio' ? (
                    <AvatarStudio
                      t={t}
                      lang={lang}
                      selectedPetId={selectedPet?.id}
                      onSelectPet={handleSelectPet}
                      equippedAccessories={equippedAccessories}
                      onChangeAccessories={(ids: string[]) => {
                        setEquippedAccessories(ids);
                        savePetPrefsToServer(selectedPet, ids);
                      }}
                      onResetToSphere={() => {
                        setSelectedPet(null);
                        savePetPrefsToServer(null, equippedAccessories);
                        toast.info(lang === 'zh' ? '已切换回原始圆球' : 'Switched back to the default sphere');
                      }}
                    />
                  ) : windowId === 'sound' ? (
                    <SoundPanel t={t} onOpenAvatarStudio={() => toggleWindow('avatar-studio')} />
                  ) : windowId === 'terminal' ? (
                    <TerminalWindow t={t} onClose={() => closeWindow('terminal')} isActive={focusedWindow === 'terminal'} />
                  ) : windowId === 'chat' ? (
                    // Chat is now fullscreen overlay — this case should not be reached
                    null
                  ) : windowId === 'growth' ? (
                    <GrowthWorkbench onLaunch={() => { setChatPrefill('小红书获客'); setChatOpen(true); }} />
                  ) : windowId === 'file-explorer' ? (
                    <SystemExplorer t={t} />
                  ) : windowId === 'calendar' ? (
                    <CalendarPanel />
                  ) : windowId === 'screenshot' ? (
                    <ScreenshotTool />
                  ) : windowId === 'stats' ? (
                    <TokenUsagePanel />
                  ) : windowId === 'ecosystem' ? (
                    <ProjectSpace
                      t={t}
                      lang={lang}
                      onOpenConversation={(id: string) => { setSearchConvId(id); setChatOpen(true); }}
                      onNavigate={toggleWindow}
                    />
                  ) : windowId === 'admin-center' ? (
                    <AdminCenter lang={lang} />
                  ) : renderTabContent(windowId)}
                </div>
              </OSWindow>
            );
          })}
        </AnimatePresence>

      </div>

        </div>
      </motion.div>

      {/* Knowledge Base fullscreen overlay */}
      <Suspense fallback={knowledgeOpen ? <div className="fixed inset-0 z-[200] flex items-center justify-center bg-[#05050a]"><div className="flex flex-col items-center gap-4"><Loader2 size={40} className="animate-spin text-amber-400" /><span className="text-xs font-black uppercase tracking-[0.3em] text-white/55">觉醒中...</span></div></div> : null}>
        <KnowledgeBase
          t={t}
          isOpen={knowledgeOpen}
          onClose={() => setKnowledgeOpen(false)}
          domain={workDomain}
        />
      </Suspense>

      {/* Chat fullscreen with sidebar */}
      {chatOpen && (
        <div id="chat-fullscreen-root" className="fixed inset-0 z-[400] flex bg-[#05050a]">
          {/* P2-15 fix: fullscreen return button */}
          <button
            onClick={() => { setChatOpen(false); setCenterView('home'); }}
            className="absolute top-3 right-3 z-[500] flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-xs text-white/60 hover:text-white/90 transition-all"
            title="返回桌面"
          >
            <ArrowLeft size={14} />
            {lang === 'zh' ? '返回桌面' : 'Desktop'}
          </button>
          <ChatSidebar
            t={t}
            user={user}
            lang={lang}
            activeConversationId={searchConvId}
            activeTab={centerView === 'chat' ? 'assistant' : centerView}
            onSelectConversation={selectConv}
            onNewChat={newChat}
            onQuickSkill={(skillName: string) => { setChatPrefill('@' + skillName + ' '); setSearchConvId(null); setCenterView('chat'); }}
            onNavigate={(tab) => {
              // WorkBuddy 风格：点侧边栏功能不弹窗、直接在中央切换面板
              if (tab === 'assistant') { setCenterView('chat'); return; }
              if (tab === 'glasses-devices') { setCenterView('glasses-devices'); return; }
              // 设置是独立 OSWindow，留在对话全屏内会被不透明全屏(z-400)盖住而"看不出反应"，故先关全屏再开设置
              if (tab === 'settings') { setChatOpen(false); toggleWindow('settings'); return; }
              setCenterView(tab);
            }}
            onReturnToDesktop={() => { setChatOpen(false); setCenterView('home'); }}
            onDeleteConversation={async (id) => {
              try { await conversationService.deleteConversation(id); }
              catch (err) { console.error('[DesktopUI] Failed to delete conversation:', err); }
            }}
          />
          <div className="relative z-[10] flex-1 min-w-0 flex flex-col">
            {centerView === 'chat' ? (
              <ErrorBoundary componentName="AgentChatPage"><AgentChatPage
                t={t}
                user={user}
                isOpen
                embedded
                personalityId={activePersonality}
                conversationId={searchConvId}
                onConversationChange={(id: string | null) => setSearchConvId(id)}
                onClose={() => { setChatOpen(false); setChatPrefill(''); }}
                prefillMessage={chatPrefill}
                onPrefillConsumed={() => setChatPrefill('')}
              /></ErrorBoundary>
            ) : (
              <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
                {centerView === 'ecosystem' && (
                  <ProjectSpace
                    t={t}
                    lang={lang}
                    onOpenConversation={(id: string) => { setSearchConvId(id); setCenterView('chat'); }}
                    onNavigate={toggleWindow}
                  />
                )}
                {centerView === 'skills' && <SkillCenter t={t} lang={lang} />}
                {centerView === 'glasses-devices' && <GlassesDeviceManager />}
                {centerView === 'automation' && (
                  <div className="mx-auto max-w-5xl space-y-6 p-6">
                    <h2 className="text-xl font-black text-white">{t.automationCenter || '自动化中心'}</h2>
                    <ExecutionWorkQueue t={t} />
                    <ReminderPanel t={t} />
                  </div>
                )}
                {centerView === 'more' && (
                  <MoreCenterGrid t={t} lang={lang} onOpen={(w) => {
                    // 有 centerView 渲染的功能（skills/ecosystem/automation/glasses-devices）全屏内切换，避免被 z-400 遮挡
                    if (w === 'skills' || w === 'ecosystem' || w === 'automation' || w === 'glasses-devices') {
                      setCenterView(w);
                    } else {
                      // 独立 OSWindow 功能先关聊天全屏再弹窗，否则被 z-400 遮挡看不见
                      setChatOpen(false);
                      toggleWindow(w);
                    }
                  }} />
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {chatOpen && typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {pendingOperationModeOption && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100010] flex items-center justify-center bg-black/55 px-4 backdrop-blur-sm"
              onClick={() => setPendingOperationMode(null)}
            >
              <motion.div
                initial={{ opacity: 0, y: 16, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 16, scale: 0.96 }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md rounded-2xl border border-cyan-400/20 bg-zinc-950/95 p-5 shadow-2xl"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cyan-400/10 text-cyan-300">
                    {pendingOperationModeOption.icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-black uppercase tracking-[0.18em] text-cyan-300">
                      {t.confirmModeSwitch || '确认切换模式'}
                    </div>
                    <h3 className="mt-1 text-lg font-black text-white">{pendingOperationModeOption.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-white/60">{pendingOperationModeOption.description}</p>
                    <div className="mt-3 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-xs leading-relaxed text-white/45">
                      {pendingOperationMode === 'meeting'
                        ? (t.modeMeetingConfirmNote || 'Meeting mode starts microphone speech-to-text, records notes, and can generate a report when you end it.')
                        : (t.modeAutoConfirmNote || 'Autonomy can use tools, run logs, teams, commands, and desktop control with visible progress and confirmations for sensitive actions.')}
                    </div>
                  </div>
                </div>
                <div className="mt-5 flex items-center justify-end gap-2">
                  <button
                    onClick={() => setPendingOperationMode(null)}
                    className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-xs font-black uppercase tracking-widest text-white/50 transition-colors hover:bg-white/10 hover:text-white"
                  >
                    {t.cancel || '取消'}
                  </button>
                  <button
                    onClick={confirmOperationModeChange}
                    className="rounded-lg border border-cyan-400/25 bg-cyan-400/15 px-4 py-2 text-xs font-black uppercase tracking-widest text-cyan-200 transition-colors hover:bg-cyan-400/25"
                  >
                    {t.enterMode || '进入模式'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Org Workbench fullscreen overlay — available to all logged-in users */}
      <AnimatePresence>
        {activeTab === 'org' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[220] bg-celestial-deep overflow-auto"
          >
            <OrgPortal
              initialMode={orgConnection?.connected ? 'select' : 'create'}
              onBack={() => setActiveTab('home')}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sanctuary — fullscreen immersive memory avatar space */}
      <Sanctuary
        agent={sanctuaryAgent}
        isOpen={sanctuaryOpen}
        onClose={() => { setSanctuaryOpen(false); setSanctuaryAgent(null); }}
      />

      {/* Memory 形象 Lab fullscreen overlay */}
      <AnimatePresence>
        {memoryLabOpen && (
          <motion.div
            initial={{ clipPath: 'circle(0% at 50% 95%)', opacity: 0 }}
            animate={{ clipPath: 'circle(150% at 50% 95%)', opacity: 1 }}
            exit={{ clipPath: 'circle(0% at 50% 95%)', opacity: 0 }}
            transition={{ duration: 0.55, ease: [0.25, 0.1, 0.25, 1] }}
            className="fixed inset-0 z-[215]"
            style={{ background: 'radial-gradient(ellipse at 50% 30%, #12081a 0%, #0a0510 40%, #020205 100%)' }}
          >
            <div className="absolute top-4 left-4 z-10">
              <button
                onClick={() => setMemoryLabOpen(false)}
                className="w-10 h-10 flex items-center justify-center bg-black/40 backdrop-blur-xl border border-white/[0.08] rounded-2xl text-white/40 hover:text-white hover:border-white/20 transition-all"
              >
                <ArrowLeft size={18} />
              </button>
            </div>
            <MemoryAvatarLab
              t={t}
              onEnterSanctuary={(agent: any) => {
                setMemoryLabOpen(false);
                setSanctuaryAgent(agent);
                setSanctuaryOpen(true);
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <ToolConfirmDialog socket={socket} isWallpaperMode={isWallpaperMode} />
      <UserSwitchPrompt socket={socket} />
      <MusicMoodLayer />
      <HelpModal isOpen={helpOpen} onClose={() => setHelpOpen(false)} />

    </div>
  );
}







